import { defineStore } from "pinia";
import { useAuthStore } from "~/stores/auth";
import { canStudentAccessLearning } from "~/utils/studentAccess";
import { useVideoCallRooms } from "~/composables/useVideoCallRooms";
import type {
  VideoCall,
  VideoCallChatCapability,
  VideoCallChatDisplayMessage,
  VideoCallChatMessage,
  VideoCallConnectionConfiguration,
  VideoCallConnectionState,
  VideoCallInvitation,
  VideoCallInvitationPreview,
  VideoCallInputDevice,
  VideoCallInputDeviceKind,
  VideoCallJoinRequest,
  VideoCallJoinRequestResponse,
  VideoCallMediaDevicesStatus,
  VideoCallParticipant,
  VideoCallPanelSize,
  VideoCallPeerDiagnostics,
  VideoCallRole,
  VideoCallStudentGoogleSignupInput,
  VideoCallStudentPasswordSignupInput,
  VideoCallStudentSignupResponse,
  VideoCallTeacherLinkStatus,
  VideoCallRoomsResponse,
} from "~/types/videoCalls";
import { DEFAULT_VIDEO_CALL_PANEL_SIZE } from "~/types/videoCalls";
import { getAdjacentVideoCallPanelSize } from "~/utils/videoCalls/panelPosition";
import type {
  VideoCallRuntime,
  VideoCallRuntimeEvent,
} from "~/utils/videoCalls/runtime.client";
import {
  activateVideoCallPrivacy,
  deactivateVideoCallPrivacy,
  type VideoCallPrivacyWindow,
} from "~/utils/videoCalls/privacy";
import {
  clearStoredVideoCallGuestSession,
  clearStoredVideoCallHostInvitation,
  getStoredVideoCallGuestSession,
  getStoredVideoCallHostInvitation,
  setStoredVideoCallGuestSession,
  setStoredVideoCallHostInvitation,
} from "~/utils/videoCalls/session";
import { getApiErrorCode, getApiErrorMessage } from "~/utils/apiError";
import {
  getScreenShareDeniedMessage,
  getScreenSharePresenterBusyMessage,
  getVideoCallErrorMessage,
  isVideoCallInvitationUnavailableError,
  VIDEO_CALL_INVITATION_UNAVAILABLE_MESSAGE,
} from "~/utils/videoCalls/errorMessages";
import {
  getAvailableMediaState,
  getVideoCallCapacityState,
} from "~/utils/videoCalls/policy";
import { buildVideoCallShareUrl } from "~/utils/videoCalls/invitation";

export type VideoCallPhase =
  | "idle"
  | "preparing"
  | "preview"
  | "waiting"
  | "active"
  | "denied"
  | "ended"
  | "error";

const DEFAULT_LIMITS = {
  warning_participants: 3,
  max_participants: 4,
};
const DEFAULT_PARTICIPANT_VOLUME_PERCENT = 100;
const JOIN_REQUEST_POLL_INTERVAL_MS = 2_000;
const HOST_JOIN_REQUEST_POLL_INTERVAL_MS = 10_000;
const CHAT_ACK_TIMEOUT_MS = 10_000;
const SCREEN_SHARE_ACK_TIMEOUT_MS = 10_000;
const SCREEN_SHARE_ACK_TIMEOUT_MESSAGE =
  "A chamada não confirmou o compartilhamento. Verifique sua conexão e tente novamente.";
const SCREEN_SHARE_PUBLICATION_FAILED_MESSAGE =
  "Não foi possível enviar sua tela aos outros participantes. Verifique sua conexão e tente novamente.";
const CAMERA_RECOVERY_FAILED_MESSAGE =
  "A câmera não voltou automaticamente. Use o controle de câmera para ligá-la novamente.";
const MICROPHONE_RECOVERY_FAILED_MESSAGE =
  "O microfone foi desconectado. Confira o dispositivo e use o controle de microfone para ligá-lo novamente.";

export type VideoCallScreenSharePhase =
  | "idle"
  | "capturing"
  | "awaiting-ack"
  | "active"
  | "stopping";

type RemoteVideoQualityState = Omit<
  Extract<VideoCallRuntimeEvent, { type: "remote-quality" }>,
  "type" | "participantId"
>;

type InitializeGuestOptions = {
  restoreStoredSession?: boolean;
};

type HostVideoCallInvitation = Omit<VideoCallInvitation, "id"> & {
  id?: string;
};

type InputRecoveryAttempt = {
  generation: number;
  promise: Promise<boolean>;
};

type ScreenShareFinalizationOptions = {
  notifyServer: boolean;
  clearLocalPresenter: boolean;
  errorMessage?: string;
};

type ScreenShareAttempt = {
  revision: number;
  lifecycle: number;
  captureReady: boolean;
  claimSent: boolean;
};

let runtime: VideoCallRuntime | null = null;
let runtimeUnsubscribe: (() => void) | null = null;
let runtimeLoading: Promise<VideoCallRuntime> | null = null;
let hostInitializationPromise: Promise<void> | null = null;
let guestBackgroundRestorationPromise: Promise<boolean> | null = null;
let guestMediaRestoration: {
  generation: number;
  promise: Promise<boolean>;
} | null = null;
let hostInvitation: HostVideoCallInvitation | null = null;
let guestSecret = "";
let guestToken = "";
let guestPollTimer: ReturnType<typeof setInterval> | null = null;
let hostPollTimer: ReturnType<typeof setInterval> | null = null;
let reconnectRetryTimer: ReturnType<typeof setTimeout> | null = null;
let rtcRefreshRetryTimer: ReturnType<typeof setTimeout> | null = null;
let refreshingRtcCredentials = false;
let reconnectInFlight = false;
let guestPollInFlight = false;
let hostPollInFlight = false;
let lifecycleGeneration = 0;
let accountLinkRevision = 0;
let accountLinkRetryRequested = false;
const chatDeliveryTimers = new Map<string, ReturnType<typeof setTimeout>>();

const resolveCall = (
  response: { call: VideoCall | null } | VideoCall | null | undefined,
): VideoCall | null => {
  if (!response) return null;
  if ("call" in response) return response.call;
  return response;
};

const resolveConnection = (
  response:
    | { connection?: VideoCallConnectionConfiguration | null }
    | null
    | undefined,
): VideoCallConnectionConfiguration | null =>
  response && "connection" in response ? (response.connection ?? null) : null;

const isApproved = (status: string) =>
  status === "approved" || status === "admitted";

const stopTimer = (timer: ReturnType<typeof setInterval> | null): null => {
  if (timer) clearInterval(timer);
  return null;
};

const stopRetryTimer = (timer: ReturnType<typeof setTimeout> | null): null => {
  if (timer) clearTimeout(timer);
  return null;
};

export const useVideoCallsStore = defineStore("videoCalls", () => {
  const api = useApi();

  const phase = ref<VideoCallPhase>("idle");
  const role = ref<VideoCallRole | null>(null);
  const call = ref<VideoCall | null>(null);
  const preview = ref<VideoCallInvitationPreview | null>(null);
  const joinRequest = ref<VideoCallJoinRequest | null>(null);
  const joinRequests = ref<VideoCallJoinRequest[]>([]);
  const participants = ref<VideoCallParticipant[]>([]);
  const connection = ref<VideoCallConnectionConfiguration | null>(null);
  const connectionState = ref<VideoCallConnectionState>("idle");
  const peerStates = ref<Record<string, RTCPeerConnectionState>>({});
  const peerDiagnostics = ref<Record<string, VideoCallPeerDiagnostics>>({});
  const remoteVideoQuality = ref<Record<string, RemoteVideoQualityState>>({});
  const videoPresentationActive = ref(true);
  const panelSize = ref<VideoCallPanelSize>(DEFAULT_VIDEO_CALL_PANEL_SIZE);
  const guestBackgroundNavigationEnabled = ref(false);
  const restoredIndividualGuestCall = ref<boolean | null>(null);
  const loading = ref(false);
  const initialized = ref(false);
  const cameraEnabled = ref(true);
  const microphoneEnabled = ref(true);
  /** Participant holding the backend-arbitrated presenter slot, if any. */
  const presenterParticipantId = ref<string | null>(null);
  /** Loudest sustained remote speaker, used by the focus size. */
  const activeSpeakerId = ref<string | null>(null);
  const screenShareBusy = ref(false);
  const screenSharePhase = ref<VideoCallScreenSharePhase>("idle");
  const screenShareError = ref("");
  const participantVolumePercent = ref<Record<string, number>>({});
  const cameraDevices = ref<VideoCallInputDevice[]>([]);
  const microphoneDevices = ref<VideoCallInputDevice[]>([]);
  const selectedCameraDeviceId = ref("");
  const selectedMicrophoneDeviceId = ref("");
  const mediaDevicesStatus = ref<VideoCallMediaDevicesStatus>("idle");
  const mediaDevicesError = ref("");
  const switchingDeviceKind = ref<VideoCallInputDeviceKind | null>(null);
  const mediaReady = ref(false);
  const mediaError = ref("");
  const errorMessage = ref("");
  const removingParticipantId = ref<string | null>(null);
  const participantRemovalError = ref("");
  const accountLinking = ref(false);
  const accountLinkError = ref("");
  const teacherLinkStatus = ref<VideoCallTeacherLinkStatus | null>(null);
  const accountSignupChecking = ref(false);
  const accountSignupError = ref("");
  const runtimeVersion = ref(0);
  const invitationRevision = ref(0);
  const chatCapability = ref<VideoCallChatCapability | null>(null);
  const chatMessages = ref<VideoCallChatDisplayMessage[]>([]);
  const chatOpen = ref(false);
  const chatVisible = ref(false);
  const chatUnreadCount = ref(0);
  let mediaDevicesRefreshRevision = 0;
  let cameraRecoveryIntent = false;
  let cameraRecoveryRetryPending = false;
  let cameraRecoveryAttempt: InputRecoveryAttempt | null = null;
  let microphoneRecoveryAttempt: InputRecoveryAttempt | null = null;
  const inputRecoveryRevision = { audioinput: 0, videoinput: 0 };
  let screenShareAttemptRevision = 0;
  let screenShareAttempt: ScreenShareAttempt | null = null;
  let screenShareAckTimer: ReturnType<typeof setTimeout> | null = null;
  let screenShareCaptureReady = false;
  let screenShareReleasedInLifecycle = false;

  const roomsController = useVideoCallRooms({
    api: api.videoCalls,
    getContext: () =>
      call.value && role.value
        ? {
            callId: call.value.id,
            role: role.value,
            joinRequestId: joinRequest.value?.id ?? "",
            guestSecret,
            generation: lifecycleGeneration,
          }
        : null,
    applyMediaSnapshot: applyRoomsMediaSnapshot,
    generateMessageId: () => generateChatMessageId(),
  });
  const { roomsSnapshot } = roomsController;

  function clearRoomMedia() {
    clearScreenShareAttempt();
    screenShareCaptureReady = false;
    screenShareReleasedInLifecycle = true;
    setScreenSharePhase("idle");
    presenterParticipantId.value = null;
    screenShareError.value = "";
    activeSpeakerId.value = null;
    peerStates.value = {};
    peerDiagnostics.value = {};
    remoteVideoQuality.value = {};
    clearChat();
    emitRuntimeRefresh();
  }

  function applyRoomsMediaSnapshot(
    payload: VideoCallRoomsResponse,
    runtimeApplied: boolean,
  ): boolean {
    if (
      !connection.value ||
      payload.participant.id !== connection.value.participant.id
    )
      return false;
    if (!runtimeApplied && runtime?.applyRoomsSnapshot(payload) === false)
      return false;
    const changedRoom =
      roomsSnapshot.value !== null &&
      (roomsSnapshot.value.current_room_id !== payload.rooms.current_room_id ||
        roomsSnapshot.value.membership_revision !==
          payload.rooms.membership_revision);
    if (changedRoom) clearRoomMedia();
    participants.value = payload.participants;
    connection.value = {
      ...connection.value,
      participant: payload.participant,
      participants: payload.participants,
      rooms: payload.rooms,
    };
    if (call.value && payload.rooms.roster) {
      call.value = {
        ...call.value,
        participant_count: payload.rooms.roster.filter(
          (participant) => !["left", "removed"].includes(participant.state),
        ).length,
      };
    }
    applyPresenter(payload.screen_share?.participant_id ?? null);
    if (payload.chat?.enabled) {
      chatCapability.value = payload.chat;
      reconcileChatSnapshot(payload.chat.messages ?? []);
    } else {
      clearChat();
    }
    emitRuntimeRefresh();
    return true;
  }

  const screenSharePending = computed(
    () =>
      screenSharePhase.value === "capturing" ||
      screenSharePhase.value === "awaiting-ack",
  );

  const clearScreenShareAckTimer = () => {
    if (screenShareAckTimer) clearTimeout(screenShareAckTimer);
    screenShareAckTimer = null;
  };

  const clearScreenShareAttempt = (attempt?: ScreenShareAttempt) => {
    if (attempt && screenShareAttempt !== attempt) return;
    clearScreenShareAckTimer();
    screenShareAttempt = null;
  };

  const isCurrentScreenShareAttempt = (attempt: ScreenShareAttempt) =>
    screenShareAttempt === attempt &&
    attempt.lifecycle === lifecycleGeneration &&
    isCurrentLifecycle(attempt.lifecycle);

  const setScreenSharePhase = (nextPhase: VideoCallScreenSharePhase) => {
    screenSharePhase.value = nextPhase;
    // Awaiting the backend is intentionally cancellable from the same control.
    screenShareBusy.value =
      nextPhase === "capturing" || nextPhase === "stopping";
  };

  const clearChat = () => {
    chatDeliveryTimers.forEach((timer) => clearTimeout(timer));
    chatDeliveryTimers.clear();
    chatCapability.value = null;
    chatMessages.value = [];
    chatOpen.value = false;
    chatVisible.value = false;
    chatUnreadCount.value = 0;
  };

  const beginLifecycle = () => {
    lifecycleGeneration += 1;
    roomsController.resetRooms();
    clearScreenShareAttempt();
    screenShareCaptureReady = false;
    screenShareReleasedInLifecycle = false;
    setScreenSharePhase("idle");
    cameraRecoveryIntent = false;
    cameraRecoveryRetryPending = false;
    cameraRecoveryAttempt = null;
    microphoneRecoveryAttempt = null;
    guestPollTimer = stopTimer(guestPollTimer);
    hostPollTimer = stopTimer(hostPollTimer);
    reconnectRetryTimer = stopRetryTimer(reconnectRetryTimer);
    rtcRefreshRetryTimer = stopRetryTimer(rtcRefreshRetryTimer);
    refreshingRtcCredentials = false;
    reconnectInFlight = false;
    guestPollInFlight = false;
    hostPollInFlight = false;
    accountLinkRevision = 0;
    accountLinkRetryRequested = false;
    accountLinking.value = false;
    teacherLinkStatus.value = null;
    accountSignupChecking.value = false;
    accountSignupError.value = "";
    removingParticipantId.value = null;
    participantRemovalError.value = "";
    presenterParticipantId.value = null;
    screenShareError.value = "";
    clearChat();
    return lifecycleGeneration;
  };

  const isCurrentLifecycle = (generation: number) =>
    generation === lifecycleGeneration;

  const limits = computed(() => connection.value?.limits ?? DEFAULT_LIMITS);
  const connectedParticipants = computed(() =>
    participants.value.filter(
      (participant) => participant.is_connected === true,
    ),
  );
  const participantCount = computed(() => {
    if (typeof call.value?.participant_count === "number") {
      return call.value.participant_count;
    }

    return Math.max(
      connectedParticipants.value.length,
      phase.value === "active" ? 1 : 0,
    );
  });
  const capacityState = computed(() =>
    getVideoCallCapacityState(participantCount.value, limits.value),
  );
  const atCapacity = computed(() => !capacityState.value.canAdmit);
  const pendingJoinRequests = computed(() =>
    joinRequests.value.filter((request) => request.status === "pending"),
  );
  const hasActiveSession = computed(
    () =>
      phase.value === "active" ||
      phase.value === "waiting" ||
      phase.value === "preparing" ||
      phase.value === "preview",
  );
  const canContinueGuestInBackground = computed(
    () =>
      guestBackgroundNavigationEnabled.value &&
      role.value === "guest" &&
      (phase.value === "waiting" || phase.value === "active") &&
      Boolean(joinRequest.value && guestSecret),
  );
  const isIndividualGuestCall = computed(() => {
    if (preview.value && "organization_id" in preview.value) {
      return preview.value.organization_id == null;
    }
    if (call.value && "organization_id" in call.value) {
      return call.value.organization_id == null;
    }
    return restoredIndividualGuestCall.value ?? false;
  });
  const chatEnabled = computed(
    () => phase.value === "active" && chatCapability.value?.enabled === true,
  );
  const chatMaxBodyLength = computed(
    () => chatCapability.value?.max_body_length ?? 500,
  );

  const emitRuntimeRefresh = () => {
    runtimeVersion.value += 1;
  };

  const setPrivacyActive = (active: boolean) => {
    if (!import.meta.client) return;

    const target = window as unknown as VideoCallPrivacyWindow;

    if (active) {
      activateVideoCallPrivacy(target);
    } else {
      deactivateVideoCallPrivacy(target);
    }
  };

  const setHostInvitation = (invitation?: HostVideoCallInvitation | null) => {
    hostInvitation = invitation ?? null;
    invitationRevision.value += 1;

    if (hostInvitation && call.value) {
      setStoredVideoCallHostInvitation({
        callId: call.value.id,
        invitationId: hostInvitation.id,
        token: hostInvitation.token,
        expiresAt: hostInvitation.expires_at,
        shareUrl: hostInvitation.share_url,
      });
    } else {
      clearStoredVideoCallHostInvitation();
    }
  };

  const shareUrl = computed(() => {
    const revision = invitationRevision.value;
    if (revision < 0) return "";
    if (!import.meta.client || !hostInvitation?.token) return "";
    return buildVideoCallShareUrl({
      token: hostInvitation.token,
      origin: window.location.origin,
      shareUrl: hostInvitation.share_url ?? hostInvitation.url,
    });
  });

  const mergeParticipant = (participant: VideoCallParticipant) => {
    if (roomsSnapshot.value) {
      const snapshot = roomsSnapshot.value;
      if (snapshot.roster) {
        snapshot.roster = [
          ...snapshot.roster.filter((item) => item.id !== participant.id),
          {
            ...snapshot.roster.find((item) => item.id === participant.id),
            ...participant,
          },
        ];
      }
      // Global presence is useful to the host but never authorizes a media peer.
      if (participant.room_id !== snapshot.current_room_id) return;
    }
    const existingParticipant = participants.value.find(
      (item) => item.id === participant.id,
    );
    const mergedParticipant = {
      ...existingParticipant,
      ...participant,
      ...(participant.account_linked === undefined &&
      existingParticipant?.account_linked !== undefined
        ? { account_linked: existingParticipant.account_linked }
        : {}),
    };
    participants.value = [
      ...participants.value.filter((item) => item.id !== participant.id),
      mergedParticipant,
    ];
    if (connection.value?.participant.id === participant.id) {
      connection.value = {
        ...connection.value,
        participant: mergedParticipant,
        participants: participants.value,
      };
    }
    runtime?.syncParticipants(participants.value);
  };

  const persistGuestRequest = (request: VideoCallJoinRequest) => {
    if (!guestSecret) {
      return;
    }

    setStoredVideoCallGuestSession({
      joinRequestId: request.id,
      guestSecret,
      callId: request.call_id,
      name: request.name,
      backgroundNavigationEnabled: guestBackgroundNavigationEnabled.value,
      individualAccountSignupAvailable: isIndividualGuestCall.value,
      ...(teacherLinkStatus.value
        ? { teacherLinkStatus: teacherLinkStatus.value }
        : {}),
    });
  };

  const removeParticipantLocally = (participantId: string) => {
    const wasOccupyingCapacity = (
      roomsSnapshot.value?.roster ?? participants.value
    ).some(
      (participant) =>
        participant.id === participantId &&
        !["left", "removed"].includes(participant.state),
    );
    if (roomsSnapshot.value?.roster) {
      roomsSnapshot.value.roster = roomsSnapshot.value.roster.filter(
        (participant) => participant.id !== participantId,
      );
    }
    participants.value = participants.value.filter(
      (participant) => participant.id !== participantId,
    );
    const nextPeerStates = { ...peerStates.value };
    delete nextPeerStates[participantId];
    peerStates.value = nextPeerStates;
    const nextPeerDiagnostics = { ...peerDiagnostics.value };
    delete nextPeerDiagnostics[participantId];
    peerDiagnostics.value = nextPeerDiagnostics;
    const nextRemoteVideoQuality = { ...remoteVideoQuality.value };
    delete nextRemoteVideoQuality[participantId];
    remoteVideoQuality.value = nextRemoteVideoQuality;
    const nextParticipantVolumePercent = {
      ...participantVolumePercent.value,
    };
    delete nextParticipantVolumePercent[participantId];
    participantVolumePercent.value = nextParticipantVolumePercent;
    runtime?.syncParticipants(participants.value);
    if (call.value && wasOccupyingCapacity) {
      call.value = {
        ...call.value,
        participant_count: Math.max(1, call.value.participant_count - 1),
      };
    }
  };

  const sortChatMessages = (
    messages: VideoCallChatDisplayMessage[],
  ): VideoCallChatDisplayMessage[] =>
    [...messages].sort((first, second) => {
      if (first.sequence !== null && second.sequence !== null) {
        return first.sequence - second.sequence;
      }
      if (first.sequence !== null) return -1;
      if (second.sequence !== null) return 1;
      return String(first.client_sent_at ?? "").localeCompare(
        String(second.client_sent_at ?? ""),
      );
    });

  const toDisplayChatMessage = (
    message: VideoCallChatMessage,
    existing?: VideoCallChatDisplayMessage,
  ): VideoCallChatDisplayMessage => ({
    sequence: message.sequence,
    message_id: message.message_id,
    participant: message.participant,
    body: message.body,
    server_sent_at: message.server_sent_at,
    ...(existing?.client_sent_at
      ? { client_sent_at: existing.client_sent_at }
      : {}),
    status: "sent",
  });

  const upsertAuthoritativeChatMessage = (
    message: VideoCallChatMessage,
    options: { countUnread?: boolean } = {},
  ) => {
    const existing = chatMessages.value.find(
      (item) =>
        item.message_id === message.message_id &&
        item.participant.id === message.participant.id,
    );
    if (existing?.status === "pending") {
      const timer = chatDeliveryTimers.get(message.message_id);
      if (timer) clearTimeout(timer);
      chatDeliveryTimers.delete(message.message_id);
    }
    chatMessages.value = sortChatMessages([
      ...chatMessages.value.filter(
        (item) =>
          item.message_id !== message.message_id ||
          item.participant.id !== message.participant.id,
      ),
      toDisplayChatMessage(message, existing),
    ]);

    if (
      options.countUnread &&
      !existing &&
      !chatVisible.value &&
      message.participant.id !== connection.value?.participant.id
    ) {
      chatUnreadCount.value += 1;
    }
  };

  const reconcileChatSnapshot = (
    messages: VideoCallChatMessage[],
    options: { countNewRemoteAsUnread?: boolean } = {},
  ) => {
    messages.forEach((message) =>
      upsertAuthoritativeChatMessage(message, {
        countUnread: options.countNewRemoteAsUnread,
      }),
    );
  };

  const setChatOpen = (open: boolean) => {
    chatOpen.value = open && chatEnabled.value;
    if (!chatOpen.value) chatVisible.value = false;
  };

  const setChatVisible = (visible: boolean) => {
    chatVisible.value = visible && chatOpen.value && chatEnabled.value;
    if (chatVisible.value) chatUnreadCount.value = 0;
  };

  const generateChatMessageId = (): string => {
    const cryptoApi = globalThis.crypto;
    if (typeof cryptoApi?.randomUUID === "function") {
      return cryptoApi.randomUUID();
    }

    const bytes = new Uint8Array(16);
    if (typeof cryptoApi?.getRandomValues === "function") {
      cryptoApi.getRandomValues(bytes);
    } else {
      for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] = Math.floor(Math.random() * 256);
      }
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (value) =>
      value.toString(16).padStart(2, "0"),
    );
    return `${hex.slice(0, 4).join("")}-${hex
      .slice(4, 6)
      .join("")}-${hex.slice(6, 8).join("")}-${hex
      .slice(8, 10)
      .join("")}-${hex.slice(10).join("")}`;
  };

  const sendPendingChatMessage = (
    message: VideoCallChatDisplayMessage,
  ): boolean => {
    const sent =
      runtime?.sendChatMessage({
        message_id: message.message_id,
        body: message.body,
        ...(message.client_sent_at
          ? { client_sent_at: message.client_sent_at }
          : {}),
      }) ?? false;

    if (!sent) {
      chatMessages.value = chatMessages.value.map((item) =>
        item.message_id === message.message_id &&
        item.participant.id === message.participant.id
          ? {
              ...item,
              status: "failed",
              error:
                "Não foi possível enviar. Verifique a conexão e tente novamente.",
            }
          : item,
      );
    } else {
      const previousTimer = chatDeliveryTimers.get(message.message_id);
      if (previousTimer) clearTimeout(previousTimer);
      chatDeliveryTimers.set(
        message.message_id,
        setTimeout(() => {
          chatDeliveryTimers.delete(message.message_id);
          chatMessages.value = chatMessages.value.map((item) =>
            item.message_id === message.message_id &&
            item.participant.id === message.participant.id &&
            item.status === "pending"
              ? {
                  ...item,
                  status: "failed",
                  error:
                    "A confirmação não chegou. Verifique a conexão e tente novamente.",
                }
              : item,
          );
        }, CHAT_ACK_TIMEOUT_MS),
      );
    }
    return sent;
  };

  const sendChatMessage = (body: string): string | null => {
    const normalizedBody = body.trim();
    const self = connection.value?.participant;
    if (
      !chatEnabled.value ||
      !self ||
      !normalizedBody ||
      normalizedBody.length > chatMaxBodyLength.value
    ) {
      return null;
    }

    const message: VideoCallChatDisplayMessage = {
      sequence: null,
      message_id: generateChatMessageId(),
      participant: {
        id: self.id,
        name: self.name,
        role: self.role,
      },
      body: normalizedBody,
      client_sent_at: new Date().toISOString(),
      status: "pending",
    };
    chatMessages.value = sortChatMessages([...chatMessages.value, message]);
    sendPendingChatMessage(message);
    return message.message_id;
  };

  const retryChatMessage = (messageId: string): boolean => {
    if (!chatEnabled.value) return false;
    const message = chatMessages.value.find(
      (item) =>
        item.message_id === messageId &&
        item.participant.id === connection.value?.participant.id &&
        item.status === "failed",
    );
    if (!message) return false;

    const pendingMessage: VideoCallChatDisplayMessage = {
      ...message,
      status: "pending",
      error: undefined,
    };
    chatMessages.value = chatMessages.value.map((item) =>
      item.message_id === messageId &&
      item.participant.id === pendingMessage.participant.id
        ? pendingMessage
        : item,
    );
    return sendPendingChatMessage(pendingMessage);
  };

  const screenShareCapability = computed(
    () => connection.value?.provider_data?.screen_share ?? null,
  );
  const screenShareAudioEnabled = computed(
    () => screenShareCapability.value?.audio !== false,
  );
  const canShareScreen = computed(() => {
    const capability = screenShareCapability.value;
    if (!capability?.enabled) return false;
    return capability.allow_guests || role.value === "host";
  });
  const isPresenting = computed(
    () =>
      presenterParticipantId.value !== null &&
      presenterParticipantId.value === connection.value?.participant.id,
  );
  const presenter = computed(
    () =>
      participants.value.find(
        (participant) => participant.id === presenterParticipantId.value,
      ) ?? null,
  );

  const setPresenterState = (participantId: string | null) => {
    presenterParticipantId.value = participantId;
    participants.value = participants.value.map((participant) =>
      Boolean(participant.screen_sharing) === (participant.id === participantId)
        ? participant
        : { ...participant, screen_sharing: participant.id === participantId },
    );
  };

  /** Applies the backend's verdict; it is the only source of truth for who presents. */
  const applyPresenter = (participantId: string | null) => {
    const selfParticipantId = connection.value?.participant.id ?? null;
    const previousPresenterId = presenterParticipantId.value;

    if (selfParticipantId !== null && participantId === selfParticipantId) {
      const attempt = screenShareAttempt;
      const awaitingCurrentAttempt = Boolean(
        attempt?.captureReady &&
        isCurrentScreenShareAttempt(attempt) &&
        screenSharePhase.value === "awaiting-ack",
      );
      const retainingActiveCapture =
        screenShareCaptureReady && screenSharePhase.value === "active";
      const restoringExistingCapture =
        screenSharePhase.value === "idle" &&
        !screenShareReleasedInLifecycle &&
        runtime?.isScreenSharing() === true;

      if (
        !awaitingCurrentAttempt &&
        !retainingActiveCapture &&
        !restoringExistingCapture
      ) {
        // This is an ACK for an attempt that was cancelled or superseded. Do
        // not resurrect its UI; hand the backend slot back immediately.
        setPresenterState(null);
        runtime?.sendScreenShareRelease();
        return;
      }

      setPresenterState(participantId);
      clearScreenShareAckTimer();
      screenShareCaptureReady = true;
      setScreenSharePhase("active");
      screenShareError.value = "";
      if (awaitingCurrentAttempt && attempt) {
        publishClaimedScreenShare(attempt).catch(() => undefined);
      }
      return;
    }

    setPresenterState(participantId);

    // A null broadcast may belong to an older presenter stopping while this
    // claim is still in flight; only our own participant id is an ACK.
    if (
      participantId === null &&
      previousPresenterId !== selfParticipantId &&
      screenSharePhase.value === "awaiting-ack"
    ) {
      return;
    }

    // The slot moved elsewhere or was cleared, so this participant must stop
    // sending even if its local capture is still running.
    if (
      runtime?.isScreenSharing() ||
      previousPresenterId === selfParticipantId ||
      screenSharePhase.value !== "idle"
    ) {
      finalizeScreenShare({
        notifyServer: false,
        clearLocalPresenter: false,
        ...(participantId && screenSharePhase.value === "awaiting-ack"
          ? {
              errorMessage: getScreenSharePresenterBusyMessage(
                participants.value.find(
                  (participant) => participant.id === participantId,
                )?.name ?? null,
              ),
            }
          : {}),
      }).catch(() => undefined);
    }
  };

  const handleServerEvent = (event: VideoCallRuntimeEvent) => {
    if (event.type === "room-media-reset") {
      clearRoomMedia();
      return;
    }
    if (event.type === "connection-state") {
      connectionState.value = event.state;
      return;
    }

    if (
      event.type === "local-stream" ||
      event.type === "remote-stream" ||
      event.type === "local-screen-stream"
    ) {
      mediaReady.value = Boolean(runtime?.getLocalStream());
      emitRuntimeRefresh();
      return;
    }

    if (event.type === "active-speaker") {
      activeSpeakerId.value = event.participantId;
      return;
    }

    if (event.type === "local-camera-ended") {
      handleLocalCameraEnded();
      return;
    }

    if (event.type === "local-microphone-ended") {
      handleLocalMicrophoneEnded();
      return;
    }

    if (event.type === "screen-share-ended") {
      // The browser's own stop control already released the capture; the
      // presenter slot still has to be handed back.
      finalizeScreenShare({
        notifyServer:
          screenShareAttempt?.claimSent === true || isPresenting.value,
        clearLocalPresenter: true,
      }).catch(() => undefined);
      return;
    }

    if (event.type === "screen-share-failed") {
      finalizeScreenShare({
        notifyServer:
          screenShareAttempt?.claimSent === true || isPresenting.value,
        clearLocalPresenter: true,
        errorMessage: getScreenShareDeniedMessage(event.reason),
      }).catch(() => undefined);
      return;
    }

    if (event.type === "peer-state") {
      peerStates.value = {
        ...peerStates.value,
        [event.participantId]: event.state,
      };
      return;
    }

    if (event.type === "peer-diagnostics") {
      if (
        !participants.value.some(
          (participant) => participant.id === event.participantId,
        )
      ) {
        return;
      }
      peerDiagnostics.value = {
        ...peerDiagnostics.value,
        [event.participantId]: event.diagnostics,
      };
      return;
    }

    if (event.type === "peer-quality") {
      if (
        !participants.value.some(
          (participant) => participant.id === event.participantId,
        )
      ) {
        return;
      }
      const previous = peerDiagnostics.value[event.participantId];
      if (previous) {
        peerDiagnostics.value = {
          ...peerDiagnostics.value,
          [event.participantId]: {
            ...previous,
            qualityProfile: event.profile,
            qualityReason: event.reason,
            audioHealth: event.audioHealth,
          },
        };
      }
      return;
    }

    if (event.type === "remote-quality") {
      remoteVideoQuality.value = {
        ...remoteVideoQuality.value,
        [event.participantId]: {
          profile: event.profile,
          reason: event.reason,
        },
      };
      return;
    }

    if (event.type === "remote-quality-reset") {
      remoteVideoQuality.value = {};
      return;
    }

    if (event.type === "reconnect-required") {
      reconnect();
      return;
    }

    if (event.type === "rtc-credentials-refresh-required") {
      refreshRtcCredentials();
      return;
    }

    if (event.type === "terminal") {
      const terminalMessages: Record<number, string> = {
        4400: "A conexão recebeu dados incompatíveis. Abra a chamada novamente.",
        4403: "Você não tem mais permissão para permanecer nesta chamada.",
        4409: "Esta chamada foi aberta em outra aba ou dispositivo.",
        4410: "Esta chamada foi encerrada ou não está mais disponível.",
        4406: "Atualize a página para entrar nesta chamada com salas.",
        4429: "Houve muitas tentativas de conexão. Aguarde um instante e tente novamente.",
      };
      errorMessage.value =
        terminalMessages[event.code] ||
        "A conexão com a chamada foi encerrada pelo servidor.";
      finishLocally("ended", { preserveError: true })
        .then(async () => {
          if (event.code !== 4403) return;
          const auth = useAuthStore();
          await auth.fetchUserData();
          if (!canStudentAccessLearning(auth.user.studentAccess)) {
            await navigateTo("/student-dashboard/requests");
          }
        })
        .catch(() => undefined);
      return;
    }

    if (event.type === "error") {
      errorMessage.value = event.message;
      return;
    }

    const serverEvent = event.event;
    if (serverEvent.type === "rooms.snapshot") {
      roomsController.applyRoomsSnapshot(serverEvent, true);
      return;
    }
    if (serverEvent.type === "session.ready") {
      call.value = call.value
        ? { ...call.value, ...serverEvent.call }
        : serverEvent.call;
      participants.value = serverEvent.participants ?? [];
      const participantIds = new Set(
        participants.value.map((participant) => participant.id),
      );
      peerDiagnostics.value = Object.fromEntries(
        Object.entries(peerDiagnostics.value).filter(([participantId]) =>
          participantIds.has(participantId),
        ),
      );
      remoteVideoQuality.value = Object.fromEntries(
        Object.entries(remoteVideoQuality.value).filter(([participantId]) =>
          participantIds.has(participantId),
        ),
      );
      joinRequests.value =
        role.value === "host" ? (serverEvent.join_requests ?? []) : [];
      connection.value = connection.value
        ? {
            ...connection.value,
            participant: serverEvent.participant,
            participants: participants.value,
            limits: serverEvent.limits ?? connection.value.limits,
            ...(serverEvent.rooms ? { rooms: serverEvent.rooms } : {}),
          }
        : connection.value;
      phase.value = "active";
      errorMessage.value = "";
      if (serverEvent.rooms) {
        roomsController.applyRoomsSnapshot(
          {
            ...serverEvent,
            rooms: serverEvent.rooms,
          },
          true,
        );
        return;
      }
      // A participant joining mid presentation learns the presenter here, and
      // a reconnect re-reads it so the slot never drifts from the backend.
      applyPresenter(serverEvent.screen_share?.participant_id ?? null);
      if (serverEvent.chat?.enabled) {
        const reconnectingChat = chatCapability.value?.enabled === true;
        chatCapability.value = serverEvent.chat;
        reconcileChatSnapshot(serverEvent.chat.messages ?? [], {
          countNewRemoteAsUnread: reconnectingChat,
        });
      } else {
        clearChat();
      }
      return;
    }

    if (serverEvent.type === "screen_share.state") {
      applyPresenter(serverEvent.participant_id ?? null);
      return;
    }

    if (serverEvent.type === "screen_share.rejected") {
      if (
        screenSharePhase.value !== "awaiting-ack" ||
        screenShareAttempt?.claimSent !== true
      ) {
        return;
      }
      // The claim lost the race, so the local capture must not stay running.
      finalizeScreenShare({
        notifyServer: false,
        clearLocalPresenter: true,
        errorMessage:
          serverEvent.reason === "presenter_busy"
            ? getScreenSharePresenterBusyMessage(
                participants.value.find(
                  (participant) =>
                    participant.id === serverEvent.participant_id,
                )?.name ?? null,
              )
            : "Você não tem permissão para compartilhar a tela nesta chamada.",
      }).catch(() => undefined);
      return;
    }

    if (serverEvent.type === "chat.message") {
      if (!chatCapability.value?.enabled) return;
      if (serverEvent.participant.id === connection.value?.participant.id) {
        const timer = chatDeliveryTimers.get(serverEvent.message_id);
        if (timer) clearTimeout(timer);
        chatDeliveryTimers.delete(serverEvent.message_id);
      }
      upsertAuthoritativeChatMessage(serverEvent, { countUnread: true });
      return;
    }

    if (serverEvent.type === "chat.message.ack") {
      if (!chatCapability.value?.enabled) return;
      const timer = chatDeliveryTimers.get(serverEvent.message.message_id);
      if (timer) clearTimeout(timer);
      chatDeliveryTimers.delete(serverEvent.message.message_id);
      upsertAuthoritativeChatMessage(serverEvent.message);
      return;
    }

    if (serverEvent.type === "chat.message.rejected") {
      const timer = chatDeliveryTimers.get(serverEvent.message_id);
      if (timer) clearTimeout(timer);
      chatDeliveryTimers.delete(serverEvent.message_id);
      chatMessages.value = chatMessages.value.map((message) =>
        message.message_id === serverEvent.message_id &&
        message.participant.id === connection.value?.participant.id
          ? {
              ...message,
              status: "failed",
              error:
                serverEvent.detail ||
                "A mensagem não pôde ser enviada. Tente novamente.",
            }
          : message,
      );
      return;
    }

    if (
      serverEvent.type === "participant.connected" ||
      serverEvent.type === "participant.updated"
    ) {
      if (!serverEvent.participant) return;
      const wasAlreadyOccupying = (
        roomsSnapshot.value?.roster ?? participants.value
      ).some(
        (participant) =>
          participant.id === serverEvent.participant?.id &&
          !["left", "removed"].includes(participant.state),
      );
      mergeParticipant(serverEvent.participant);
      if (
        serverEvent.type === "participant.connected" &&
        call.value &&
        !wasAlreadyOccupying
      ) {
        call.value = {
          ...call.value,
          participant_count: call.value.participant_count + 1,
        };
      }
      return;
    }

    if (serverEvent.type === "participant.disconnected") {
      const participantId =
        serverEvent.participant_id || serverEvent.participant?.id || "";
      if (serverEvent.participant) {
        mergeParticipant(serverEvent.participant);
      } else if (participantId) {
        const participant = participants.value.find(
          (item) => item.id === participantId,
        );
        if (participant) {
          mergeParticipant({
            ...participant,
            state: "disconnected",
            is_connected: false,
          });
        }
      }
      return;
    }

    if (
      serverEvent.type === "participant.left" ||
      serverEvent.type === "participant.removed"
    ) {
      const participantId =
        serverEvent.participant_id || serverEvent.participant?.id || "";
      if (participantId === connection.value?.participant.id) {
        finishLocally("ended");
        return;
      }
      if (participantId) removeParticipantLocally(participantId);
      return;
    }

    if (
      serverEvent.type === "join_request.created" ||
      serverEvent.type === "join_request.updated"
    ) {
      joinRequests.value = [
        ...joinRequests.value.filter(
          (request) => request.id !== serverEvent.join_request.id,
        ),
        serverEvent.join_request,
      ];
      return;
    }

    if (serverEvent.type === "call.ended") {
      finishLocally("ended");
    }
  };

  const ensureRuntime = async (): Promise<VideoCallRuntime> => {
    if (runtime) {
      runtimeUnsubscribe?.();
      runtimeUnsubscribe = runtime.subscribe(handleServerEvent);
      runtime.setVideoPresentationActive(videoPresentationActive.value);
      return runtime;
    }

    runtimeLoading ??= import("~/utils/videoCalls/runtime.client").then(
      ({ getVideoCallRuntime }) => getVideoCallRuntime(),
    );
    runtime = await runtimeLoading;
    runtimeUnsubscribe?.();
    runtimeUnsubscribe = runtime.subscribe(handleServerEvent);
    runtime.setVideoPresentationActive(videoPresentationActive.value);
    return runtime;
  };

  const getActiveInputDeviceId = (kind: VideoCallInputDeviceKind): string => {
    const localStream = runtime?.getLocalStream();
    const track =
      kind === "audioinput"
        ? localStream?.getAudioTracks()[0]
        : localStream?.getVideoTracks()[0];
    return track?.getSettings?.().deviceId ?? "";
  };

  const resolveSelectedDeviceId = (
    devices: VideoCallInputDevice[],
    activeDeviceId: string,
    selectedDeviceId: string,
  ): string => {
    if (devices.some((device) => device.deviceId === activeDeviceId)) {
      return activeDeviceId;
    }
    if (devices.some((device) => device.deviceId === selectedDeviceId)) {
      return selectedDeviceId;
    }
    // Do not present another input as active while its track was never opened.
    return activeDeviceId || selectedDeviceId
      ? ""
      : (devices[0]?.deviceId ?? "");
  };

  const refreshMediaDevices = async (
    generation = lifecycleGeneration,
    currentRuntime?: VideoCallRuntime,
  ): Promise<void> => {
    if (typeof navigator === "undefined" || !isCurrentLifecycle(generation)) {
      return;
    }

    const refreshRevision = ++mediaDevicesRefreshRevision;
    mediaDevicesStatus.value = "loading";
    mediaDevicesError.value = "";
    try {
      const activeRuntime = currentRuntime ?? (await ensureRuntime());
      const devices = await activeRuntime.getInputDevices();
      if (
        !isCurrentLifecycle(generation) ||
        refreshRevision !== mediaDevicesRefreshRevision
      ) {
        return;
      }

      cameraDevices.value = devices.filter(
        (device) => device.kind === "videoinput",
      );
      microphoneDevices.value = devices.filter(
        (device) => device.kind === "audioinput",
      );
      selectedCameraDeviceId.value = resolveSelectedDeviceId(
        cameraDevices.value,
        getActiveInputDeviceId("videoinput"),
        selectedCameraDeviceId.value,
      );
      selectedMicrophoneDeviceId.value = resolveSelectedDeviceId(
        microphoneDevices.value,
        getActiveInputDeviceId("audioinput"),
        selectedMicrophoneDeviceId.value,
      );
      mediaDevicesStatus.value = "ready";
    } catch (error) {
      if (
        !isCurrentLifecycle(generation) ||
        refreshRevision !== mediaDevicesRefreshRevision
      ) {
        return;
      }
      const unsupported = !navigator.mediaDevices?.enumerateDevices;
      mediaDevicesStatus.value = unsupported ? "unsupported" : "error";
      mediaDevicesError.value = unsupported
        ? "Este navegador não permite escolher câmera e microfone."
        : error instanceof Error
          ? error.message
          : "Não foi possível carregar os dispositivos disponíveis.";
    }
  };

  const requestMedia = async (
    generation = lifecycleGeneration,
    requiredKind?: "audio" | "video",
  ): Promise<boolean> => {
    if (!isCurrentLifecycle(generation)) return false;
    mediaError.value = "";
    try {
      const activeRuntime = await ensureRuntime();
      if (!isCurrentLifecycle(generation)) {
        activeRuntime.disconnect({ stopMedia: true });
        return false;
      }
      const localStream = await activeRuntime.ensureLocalMedia(requiredKind);
      if (!isCurrentLifecycle(generation)) {
        activeRuntime.disconnect({ stopMedia: true });
        return false;
      }
      const availableMedia = getAvailableMediaState(localStream);
      if (!availableMedia.microphoneAvailable) {
        microphoneEnabled.value = false;
      }
      if (!availableMedia.cameraAvailable) {
        cameraEnabled.value = false;
      }
      activeRuntime.setTrackEnabled("audio", microphoneEnabled.value);
      activeRuntime.setTrackEnabled("video", cameraEnabled.value);
      mediaReady.value = true;
      await refreshMediaDevices(generation, activeRuntime);
      return true;
    } catch (error) {
      if (!isCurrentLifecycle(generation)) return false;
      const availableMedia = getAvailableMediaState(
        runtime?.getLocalStream() ?? null,
      );
      mediaReady.value =
        availableMedia.cameraAvailable || availableMedia.microphoneAvailable;
      if (!requiredKind || requiredKind === "video") {
        cameraEnabled.value = false;
      }
      if (!requiredKind || requiredKind === "audio") {
        microphoneEnabled.value = false;
      }
      if (!availableMedia.cameraAvailable) {
        cameraEnabled.value = false;
      }
      if (!availableMedia.microphoneAvailable) {
        microphoneEnabled.value = false;
      }
      mediaError.value =
        error instanceof Error
          ? error.message
          : "Não foi possível acessar a câmera e o microfone.";
      return false;
    }
  };

  const startGuestMediaRestoration = (generation: number): Promise<boolean> => {
    if (guestMediaRestoration?.generation === generation) {
      return guestMediaRestoration.promise;
    }

    const restoration = {
      generation,
      promise: requestMedia(generation),
    };
    guestMediaRestoration = restoration;
    restoration.promise.finally(() => {
      if (guestMediaRestoration === restoration) {
        guestMediaRestoration = null;
      }
    });
    return restoration.promise;
  };

  const connectRuntime = async (
    nextConnection: VideoCallConnectionConfiguration,
    generation = lifecycleGeneration,
  ): Promise<boolean> => {
    if (!isCurrentLifecycle(generation)) return false;
    const activeRuntime = await ensureRuntime();
    if (!isCurrentLifecycle(generation)) {
      activeRuntime.disconnect({ stopMedia: true });
      return false;
    }
    connection.value = nextConnection;
    participants.value = nextConnection.participants ?? [];
    if (nextConnection.rooms)
      roomsController.applyRoomsSnapshot(
        {
          rooms: nextConnection.rooms,
          participant: nextConnection.participant,
          participants: nextConnection.participants,
        },
        true,
      );
    await activeRuntime.connect(nextConnection);
    if (!isCurrentLifecycle(generation)) {
      activeRuntime.disconnect({ stopMedia: true });
      return false;
    }
    phase.value = "active";
    errorMessage.value = "";
    return true;
  };

  const refreshHostJoinRequests = async (generation = lifecycleGeneration) => {
    if (!call.value || role.value !== "host") return;
    if (hostPollInFlight || !isCurrentLifecycle(generation)) return;

    hostPollInFlight = true;
    const callId = call.value.id;
    try {
      const nextRequests = await api.videoCalls.getJoinRequests(callId);
      if (isCurrentLifecycle(generation)) {
        joinRequests.value = nextRequests;
      }
    } catch {
      // WebSocket events remain the primary path; polling is a resilience path.
    } finally {
      if (isCurrentLifecycle(generation)) {
        hostPollInFlight = false;
      }
    }
  };

  const startHostPolling = (generation = lifecycleGeneration) => {
    hostPollTimer = stopTimer(hostPollTimer);
    hostPollTimer = setInterval(() => {
      if (isCurrentLifecycle(generation)) {
        refreshHostJoinRequests(generation);
      }
    }, HOST_JOIN_REQUEST_POLL_INTERVAL_MS);
  };

  const connectHost = async (
    generation = lifecycleGeneration,
  ): Promise<boolean> => {
    if (!call.value || !isCurrentLifecycle(generation)) return false;

    const callId = call.value.id;
    const response = await api.videoCalls.updateHostConnection(
      callId,
      "connected",
    );
    if (!isCurrentLifecycle(generation)) return false;
    const nextConnection = resolveConnection(response);
    if (!nextConnection) {
      throw new Error("O servidor não retornou a configuração da chamada.");
    }
    if (!(await connectRuntime(nextConnection, generation))) return false;
    await refreshHostJoinRequests(generation);
    if (!isCurrentLifecycle(generation)) return false;
    startHostPolling(generation);
    errorMessage.value = "";
    return true;
  };

  const restoreHostInvitation = () => {
    if (!call.value) return;
    const stored = getStoredVideoCallHostInvitation();
    const currentInvitationId = call.value.invitation?.id;
    const invitationIdsMatch =
      !stored?.invitationId ||
      !currentInvitationId ||
      stored.invitationId === currentInvitationId;
    if (
      stored?.callId === call.value.id &&
      invitationIdsMatch &&
      new Date(stored.expiresAt).getTime() > Date.now()
    ) {
      const restoredInvitationId = stored.invitationId ?? currentInvitationId;
      setHostInvitation({
        ...(restoredInvitationId ? { id: restoredInvitationId } : {}),
        token: stored.token,
        expires_at: stored.expiresAt,
        share_url: stored.shareUrl,
      });
    } else {
      setHostInvitation(null);
    }
  };

  const runHostInitialization = async () => {
    const generation = beginLifecycle();
    initialized.value = true;
    loading.value = true;
    errorMessage.value = "";
    setPrivacyActive(true);

    try {
      const response = await api.videoCalls.getCurrentCall();
      if (!isCurrentLifecycle(generation)) return;
      const currentCall = resolveCall(response);
      if (!currentCall || currentCall.status !== "active") {
        clearStoredVideoCallHostInvitation();
        setPrivacyActive(false);
        return;
      }

      role.value = "host";
      call.value = currentCall;
      participants.value = currentCall.participants ?? [];
      restoreHostInvitation();
      phase.value = "preparing";
      await requestMedia(generation);
      if (!isCurrentLifecycle(generation)) return;
      await connectHost(generation);
    } catch (error) {
      if (!isCurrentLifecycle(generation)) return;
      const status = (error as { response?: { status?: number } }).response
        ?.status;
      if (status !== 404) {
        errorMessage.value = getApiErrorMessage(
          error,
          "Não foi possível restaurar a chamada.",
        );
      }

      if (call.value?.status === "active") {
        role.value = "host";
        phase.value = "preview";
        panelSize.value = DEFAULT_VIDEO_CALL_PANEL_SIZE;
        setPrivacyActive(true);
      } else if (status === 404) {
        await finishLocally("idle");
      } else {
        await finishLocally("error", { preserveError: true });
      }
    } finally {
      if (isCurrentLifecycle(generation)) {
        loading.value = false;
      }
    }
  };

  const initializeHost = async () => {
    if (role.value === "guest") return;
    if (hostInitializationPromise) {
      await hostInitializationPromise;
      return;
    }
    if (initialized.value) return;

    const currentInitialization = runHostInitialization();
    hostInitializationPromise = currentInitialization;
    try {
      await currentInitialization;
    } finally {
      if (hostInitializationPromise === currentInitialization) {
        hostInitializationPromise = null;
      }
    }
  };

  const prepareHostCall = async () => {
    if (loading.value || phase.value !== "idle") return;

    const generation = beginLifecycle();
    role.value = "host";
    phase.value = "preparing";
    loading.value = true;
    errorMessage.value = "";
    setPrivacyActive(true);

    try {
      await requestMedia(generation);
      if (!isCurrentLifecycle(generation)) return;
      phase.value = "preview";
      panelSize.value = DEFAULT_VIDEO_CALL_PANEL_SIZE;
    } finally {
      if (isCurrentLifecycle(generation)) {
        loading.value = false;
      }
    }
  };

  const cancelHostPreparation = async () => {
    const generation = lifecycleGeneration;
    const activeCallId =
      call.value?.status === "active" ? call.value.id : undefined;
    errorMessage.value = "";

    if (!activeCallId) {
      await finishLocally("idle");
      return;
    }

    loading.value = true;
    try {
      await api.videoCalls.endCall(activeCallId);
      if (!isCurrentLifecycle(generation)) return;
      await finishLocally("ended");
    } catch (error) {
      if (!isCurrentLifecycle(generation)) return;
      errorMessage.value = getApiErrorMessage(
        error,
        "Não foi possível encerrar a chamada recuperada.",
      );
      phase.value = "preview";
      panelSize.value = DEFAULT_VIDEO_CALL_PANEL_SIZE;
    } finally {
      if (isCurrentLifecycle(generation)) {
        loading.value = false;
      }
    }
  };

  const startHostCall = async () => {
    if (loading.value) return;
    const generation = beginLifecycle();
    loading.value = true;
    errorMessage.value = "";

    try {
      if (call.value?.status === "active") {
        await connectHost(generation);
        if (!isCurrentLifecycle(generation)) return;
        panelSize.value = DEFAULT_VIDEO_CALL_PANEL_SIZE;
        return;
      }

      const response = await api.videoCalls.createCall();
      if (!isCurrentLifecycle(generation)) {
        const staleCall = resolveCall(response);
        if (staleCall) {
          api.videoCalls.endCall(staleCall.id).catch(() => undefined);
        }
        return;
      }
      call.value = resolveCall(response);
      if (!call.value) {
        throw new Error("O servidor não retornou a chamada criada.");
      }
      role.value = "host";
      setHostInvitation(response.invitation);
      await connectHost(generation);
      if (!isCurrentLifecycle(generation)) return;
      panelSize.value = DEFAULT_VIDEO_CALL_PANEL_SIZE;
    } catch (error) {
      if (!isCurrentLifecycle(generation)) return;
      try {
        const currentResponse = await api.videoCalls.getCurrentCall();
        if (!isCurrentLifecycle(generation)) return;
        const persistedCall = resolveCall(currentResponse);
        if (persistedCall?.status === "active") {
          call.value = persistedCall;
          role.value = "host";
          restoreHostInvitation();
          await connectHost(generation);
          if (!isCurrentLifecycle(generation)) return;
          panelSize.value = DEFAULT_VIDEO_CALL_PANEL_SIZE;
          return;
        }
      } catch {
        // Preserve the original create error when recovery is unavailable.
      }

      errorMessage.value = getApiErrorMessage(
        error,
        "Não foi possível iniciar a chamada.",
      );
      phase.value = "preview";
    } finally {
      if (isCurrentLifecycle(generation)) {
        loading.value = false;
      }
    }
  };

  const rotateInvitation = async () => {
    if (!call.value || loading.value) return;
    const generation = lifecycleGeneration;
    const callId = call.value.id;
    loading.value = true;
    errorMessage.value = "";

    try {
      const invitation = await api.videoCalls.rotateInvitation(callId);
      if (!isCurrentLifecycle(generation)) return;
      setHostInvitation(invitation);
      await refreshHostJoinRequests(generation);
      if (isCurrentLifecycle(generation)) {
        errorMessage.value = "";
      }
    } catch (error) {
      if (!isCurrentLifecycle(generation)) return;
      errorMessage.value = getApiErrorMessage(
        error,
        "Não foi possível gerar um novo link.",
      );
    } finally {
      if (isCurrentLifecycle(generation)) {
        loading.value = false;
      }
    }
  };

  const approveJoinRequest = async (requestId: string) => {
    if (!call.value || atCapacity.value) return;
    const generation = lifecycleGeneration;
    const callId = call.value.id;
    loading.value = true;
    errorMessage.value = "";

    try {
      const response = await api.videoCalls.approveJoinRequest(
        callId,
        requestId,
      );
      if (!isCurrentLifecycle(generation)) return;
      if (response.participant) {
        const wasAlreadyOccupying = (
          roomsSnapshot.value?.roster ?? participants.value
        ).some(
          (participant) =>
            participant.id === response.participant?.id &&
            !["left", "removed"].includes(participant.state),
        );
        mergeParticipant(response.participant);
        if (call.value && !wasAlreadyOccupying) {
          call.value = {
            ...call.value,
            participant_count: call.value.participant_count + 1,
          };
        }
      }
      await refreshHostJoinRequests(generation);
      if (isCurrentLifecycle(generation)) {
        errorMessage.value = "";
      }
    } catch (error) {
      if (!isCurrentLifecycle(generation)) return;
      const code = getApiErrorCode(error);
      errorMessage.value =
        code === "video_call_full" || code === "video_call_capacity_reached"
          ? "A chamada já atingiu o limite de quatro participantes."
          : getApiErrorMessage(
              error,
              "Não foi possível admitir este participante.",
            );
    } finally {
      if (isCurrentLifecycle(generation)) {
        loading.value = false;
      }
    }
  };

  const denyJoinRequest = async (requestId: string) => {
    if (!call.value) return;
    const generation = lifecycleGeneration;
    const callId = call.value.id;
    loading.value = true;
    errorMessage.value = "";
    try {
      await api.videoCalls.denyJoinRequest(callId, requestId);
      if (!isCurrentLifecycle(generation)) return;
      await refreshHostJoinRequests(generation);
      if (isCurrentLifecycle(generation)) {
        errorMessage.value = "";
      }
    } catch (error) {
      if (!isCurrentLifecycle(generation)) return;
      errorMessage.value = getApiErrorMessage(
        error,
        "Não foi possível recusar este participante.",
      );
    } finally {
      if (isCurrentLifecycle(generation)) {
        loading.value = false;
      }
    }
  };

  const clearParticipantRemovalError = (): void => {
    participantRemovalError.value = "";
  };

  const removeParticipant = async (participantId: string): Promise<boolean> => {
    const participant = (
      roomsSnapshot.value?.roster ?? participants.value
    ).find((item) => item.id === participantId);
    if (
      !call.value ||
      role.value !== "host" ||
      !participant ||
      participant.role === "host" ||
      removingParticipantId.value
    ) {
      return false;
    }
    const generation = lifecycleGeneration;
    const callId = call.value.id;
    removingParticipantId.value = participantId;
    participantRemovalError.value = "";
    try {
      await api.videoCalls.removeParticipant(callId, participantId);
      if (!isCurrentLifecycle(generation)) return false;
      removeParticipantLocally(participantId);
      participantRemovalError.value = "";
      return true;
    } catch (error) {
      if (!isCurrentLifecycle(generation)) return false;
      participantRemovalError.value = getApiErrorMessage(
        error,
        "Não foi possível remover este participante.",
      );
      return false;
    } finally {
      if (
        isCurrentLifecycle(generation) &&
        removingParticipantId.value === participantId
      ) {
        removingParticipantId.value = null;
      }
    }
  };

  const pollGuestJoinRequest = async (generation = lifecycleGeneration) => {
    if (
      !joinRequest.value ||
      !guestSecret ||
      guestPollInFlight ||
      !isCurrentLifecycle(generation)
    ) {
      return;
    }

    guestPollInFlight = true;
    const requestId = joinRequest.value.id;
    const currentGuestSecret = guestSecret;
    const linkRevisionAtRequestStart = accountLinkRevision;
    try {
      const response = await api.videoCalls.getGuestJoinRequest(
        requestId,
        currentGuestSecret,
      );
      if (!isCurrentLifecycle(generation)) return;
      errorMessage.value = "";
      const currentRequest = joinRequest.value;
      const shouldPreserveLinkedIdentity =
        currentRequest?.account_linked === true &&
        response.join_request.account_linked !== true &&
        linkRevisionAtRequestStart !== accountLinkRevision &&
        ["pending", "approved"].includes(response.join_request.status);
      joinRequest.value = shouldPreserveLinkedIdentity
        ? {
            ...response.join_request,
            name: currentRequest?.name ?? response.join_request.name,
            account_linked: true,
          }
        : response.join_request;
      if (joinRequest.value.account_linked === true) {
        accountLinkError.value = "";
      }
      persistGuestRequest(joinRequest.value);
      call.value = response.call ?? call.value;

      if (isApproved(response.join_request.status)) {
        guestPollTimer = stopTimer(guestPollTimer);
        await startGuestMediaRestoration(generation);
        if (!isCurrentLifecycle(generation)) return;
        const connectionResponse = await api.videoCalls.updateGuestConnection(
          response.join_request.id,
          currentGuestSecret,
          "connected",
        );
        if (!isCurrentLifecycle(generation)) return;
        const nextConnection =
          resolveConnection(connectionResponse) ??
          resolveConnection(response) ??
          response.join_request.connection ??
          null;
        if (!nextConnection) {
          throw new Error("O servidor não retornou a configuração da chamada.");
        }
        const privateAccountLinked =
          connectionResponse.participant?.account_linked ??
          nextConnection.participant.account_linked;
        if (joinRequest.value && typeof privateAccountLinked === "boolean") {
          joinRequest.value = {
            ...joinRequest.value,
            account_linked: privateAccountLinked,
          };
        }
        if (!(await connectRuntime(nextConnection, generation))) return;
        panelSize.value = guestBackgroundNavigationEnabled.value
          ? "small"
          : DEFAULT_VIDEO_CALL_PANEL_SIZE;
      } else if (response.join_request.status === "denied") {
        guestPollTimer = stopTimer(guestPollTimer);
        await finishLocally("denied");
      } else if (response.join_request.status === "expired") {
        guestPollTimer = stopTimer(guestPollTimer);
        await finishLocally("ended");
      } else if (
        response.join_request.status === "cancelled" ||
        response.join_request.status === "canceled"
      ) {
        guestPollTimer = stopTimer(guestPollTimer);
        await finishLocally("ended");
      }
    } catch (error) {
      if (!isCurrentLifecycle(generation)) return;
      const status = (error as { response?: { status?: number } }).response
        ?.status;
      if ([401, 403, 404, 410].includes(status ?? 0)) {
        guestPollTimer = stopTimer(guestPollTimer);
        await finishLocally("ended");
      } else {
        errorMessage.value = getApiErrorMessage(
          error,
          "Não foi possível atualizar a sala de espera.",
        );
      }
    } finally {
      if (isCurrentLifecycle(generation)) {
        guestPollInFlight = false;
      }
    }
  };

  const startGuestPolling = (generation = lifecycleGeneration) => {
    guestPollTimer = stopTimer(guestPollTimer);
    guestPollTimer = setInterval(() => {
      if (isCurrentLifecycle(generation)) {
        pollGuestJoinRequest(generation);
      }
    }, JOIN_REQUEST_POLL_INTERVAL_MS);
    pollGuestJoinRequest(generation);
  };

  const initializeGuest = async (
    token: string,
    options: InitializeGuestOptions = {},
  ) => {
    if (role.value === "host" && hasActiveSession.value) {
      return;
    }
    if (
      !token &&
      role.value === "guest" &&
      (phase.value === "waiting" || phase.value === "active") &&
      joinRequest.value &&
      guestSecret
    ) {
      return;
    }

    const generation = beginLifecycle();
    role.value = "guest";
    guestToken = token;
    setPrivacyActive(true);
    loading.value = true;
    errorMessage.value = "";
    accountLinkError.value = "";

    try {
      if (token) {
        guestBackgroundNavigationEnabled.value = false;
        restoredIndividualGuestCall.value = null;
        clearStoredVideoCallGuestSession();
        guestSecret = "";
        joinRequest.value = null;
      }

      const stored =
        token || options.restoreStoredSession === false
          ? null
          : getStoredVideoCallGuestSession();
      if (stored) {
        guestBackgroundNavigationEnabled.value =
          stored.backgroundNavigationEnabled === true;
        restoredIndividualGuestCall.value =
          stored.individualAccountSignupAvailable ?? null;
        teacherLinkStatus.value = stored.teacherLinkStatus ?? null;
        guestSecret = stored.guestSecret;
        joinRequest.value = {
          id: stored.joinRequestId,
          call_id: stored.callId,
          name: stored.name,
          status: "pending",
          created_at: "",
        };
        phase.value = "waiting";
        startGuestMediaRestoration(generation);
        startGuestPolling(generation);
        return;
      }

      if (!token) {
        throw new Error("Este link de chamada é inválido ou já foi removido.");
      }

      const invitationPreview = await api.videoCalls.previewInvitation(token);
      if (!isCurrentLifecycle(generation)) return;
      preview.value = invitationPreview;
      teacherLinkStatus.value =
        invitationPreview.organization_id == null ? null : "not_applicable";
      phase.value = "idle";
    } catch (error) {
      if (!isCurrentLifecycle(generation)) return;
      phase.value = "error";
      role.value = null;
      guestToken = "";
      preview.value = null;
      setPrivacyActive(false);
      errorMessage.value = isVideoCallInvitationUnavailableError(error)
        ? VIDEO_CALL_INVITATION_UNAVAILABLE_MESSAGE
        : error instanceof Error && !("response" in error)
          ? error.message
          : getVideoCallErrorMessage(
              error,
              "Não foi possível verificar este convite. Confira o link ou tente novamente em instantes.",
            );
    } finally {
      if (isCurrentLifecycle(generation)) {
        loading.value = false;
      }
    }
  };

  const enableGuestBackgroundNavigation = (): boolean => {
    if (
      role.value !== "guest" ||
      (phase.value !== "waiting" && phase.value !== "active") ||
      !joinRequest.value ||
      !guestSecret
    ) {
      return false;
    }

    guestBackgroundNavigationEnabled.value = true;
    panelSize.value = "small";
    setChatOpen(false);
    persistGuestRequest(joinRequest.value);
    setPrivacyActive(true);
    return true;
  };

  const runGuestBackgroundRestoration = (): boolean => {
    const stored = getStoredVideoCallGuestSession();
    if (
      stored?.backgroundNavigationEnabled !== true ||
      !stored.joinRequestId ||
      !stored.guestSecret ||
      !stored.callId
    ) {
      return false;
    }

    if (role.value === "host" && hasActiveSession.value) {
      return false;
    }

    if (
      role.value === "guest" &&
      (phase.value === "waiting" || phase.value === "active") &&
      joinRequest.value &&
      guestSecret
    ) {
      if (joinRequest.value.id !== stored.joinRequestId) {
        return false;
      }
      guestBackgroundNavigationEnabled.value = true;
      panelSize.value = "small";
      setPrivacyActive(true);
      return true;
    }

    const generation = beginLifecycle();
    role.value = "guest";
    guestToken = "";
    guestSecret = stored.guestSecret;
    guestBackgroundNavigationEnabled.value = true;
    restoredIndividualGuestCall.value =
      stored.individualAccountSignupAvailable ?? null;
    teacherLinkStatus.value = stored.teacherLinkStatus ?? null;
    joinRequest.value = {
      id: stored.joinRequestId,
      call_id: stored.callId,
      name: stored.name,
      status: "pending",
      created_at: "",
    };
    phase.value = "waiting";
    panelSize.value = "small";
    loading.value = true;
    errorMessage.value = "";
    accountLinkError.value = "";
    setPrivacyActive(true);

    try {
      startGuestMediaRestoration(generation);
      startGuestPolling(generation);
      return true;
    } finally {
      if (isCurrentLifecycle(generation)) {
        loading.value = false;
      }
    }
  };

  const restoreGuestBackgroundSession = async (): Promise<boolean> => {
    guestBackgroundRestorationPromise ??= Promise.resolve(
      runGuestBackgroundRestoration(),
    );
    const currentRestoration = guestBackgroundRestorationPromise;
    try {
      return await currentRestoration;
    } finally {
      if (guestBackgroundRestorationPromise === currentRestoration) {
        guestBackgroundRestorationPromise = null;
      }
    }
  };

  const joinAsGuest = async (name: string) => {
    const normalizedName = name.trim().slice(0, 100);
    if (!normalizedName || !guestToken) return;
    const generation = beginLifecycle();
    const invitationToken = guestToken;
    loading.value = true;
    errorMessage.value = "";

    try {
      await requestMedia(generation);
      if (!isCurrentLifecycle(generation)) return;
      const response = await api.videoCalls.joinInvitation(
        invitationToken,
        normalizedName,
      );
      if (!isCurrentLifecycle(generation)) {
        api.videoCalls
          .updateGuestConnection(
            response.join_request.id,
            response.guest_secret,
            "left",
          )
          .catch(() => undefined);
        return;
      }
      joinRequest.value = response.join_request;
      guestSecret = response.guest_secret;
      teacherLinkStatus.value = isIndividualGuestCall.value
        ? "waiting_for_admission"
        : "not_applicable";
      persistGuestRequest(response.join_request);
      guestToken = "";
      phase.value = "waiting";
      startGuestPolling(generation);
    } catch (error) {
      if (!isCurrentLifecycle(generation)) return;
      if (isVideoCallInvitationUnavailableError(error)) {
        errorMessage.value = VIDEO_CALL_INVITATION_UNAVAILABLE_MESSAGE;
        await finishLocally("error", { preserveError: true });
        return;
      }

      errorMessage.value = getVideoCallErrorMessage(
        error,
        "Não foi possível entrar na sala de espera. Tente novamente em instantes.",
      );
    } finally {
      if (isCurrentLifecycle(generation)) {
        loading.value = false;
      }
    }
  };

  const clearGuestAccountErrors = (): void => {
    accountLinkError.value = "";
    accountSignupError.value = "";
  };

  const linkGuestAccount = async (): Promise<boolean> => {
    if (role.value !== "guest" || !joinRequest.value || !guestSecret) {
      return false;
    }

    if (accountLinking.value) {
      accountLinkRetryRequested = true;
      return false;
    }

    const generation = lifecycleGeneration;
    const requestId = joinRequest.value.id;
    const currentGuestSecret = guestSecret;
    accountLinking.value = true;
    clearGuestAccountErrors();
    let linked = false;

    try {
      const response = await api.videoCalls.linkGuestAccount(
        requestId,
        currentGuestSecret,
      );
      if (!isCurrentLifecycle(generation)) {
        return false;
      }

      applyGuestAccountResponse(response);
      linked = true;
    } catch (error) {
      if (!isCurrentLifecycle(generation)) {
        return false;
      }

      if (getApiErrorCode(error) === "student_limit_reached") {
        teacherLinkStatus.value = "capacity_reached";
        accountLinkError.value = "";
      } else {
        accountLinkError.value = getVideoCallErrorMessage(
          error,
          isVideoCallInvitationUnavailableError(error)
            ? "Sua conta está conectada, mas a solicitação de entrada não está mais disponível. Peça ao professor um novo link para entrar na chamada."
            : "Sua conta está conectada, mas não foi possível vinculá-la à chamada. Você continua participando como convidado.",
        );
      }
    } finally {
      if (isCurrentLifecycle(generation)) {
        accountLinking.value = false;
      }
    }

    if (!isCurrentLifecycle(generation)) {
      return false;
    }

    const shouldRetryAfterAdmission =
      accountLinkRetryRequested &&
      phase.value === "active" &&
      teacherLinkStatus.value === "waiting_for_admission";
    accountLinkRetryRequested = false;

    if (shouldRetryAfterAdmission) {
      return await linkGuestAccount();
    }

    return linked;
  };

  function applyGuestAccountResponse(
    response: VideoCallJoinRequestResponse,
  ): void {
    accountLinkRevision += 1;
    joinRequest.value = response.join_request;
    call.value = response.call ?? call.value;
    teacherLinkStatus.value =
      response.teacher_link_status ??
      (response.join_request.account_linked === true ? "linked" : null);
    persistGuestRequest(response.join_request);

    if (response.participant) {
      mergeParticipant({
        ...response.participant,
        account_linked: response.join_request.account_linked,
      });
    } else if (
      connection.value &&
      connection.value.participant.role === "guest"
    ) {
      mergeParticipant({
        ...connection.value.participant,
        name: response.join_request.name,
        account_linked: response.join_request.account_linked,
      });
    }

    if (teacherLinkStatus.value === "linked") {
      accountLinkError.value = "";
      accountSignupError.value = "";
    }
  }

  const applyAccountSignupError = (error: unknown): void => {
    if (getApiErrorCode(error) === "student_limit_reached") {
      teacherLinkStatus.value = "capacity_reached";
      accountSignupError.value = "";
      return;
    }

    accountSignupError.value = getVideoCallErrorMessage(
      error,
      "Não foi possível criar sua conta agora. Tente novamente em instantes.",
    );
  };

  const checkGuestStudentSignupEligibility = async (): Promise<boolean> => {
    if (
      role.value !== "guest" ||
      !joinRequest.value ||
      !guestSecret ||
      accountSignupChecking.value
    ) {
      return false;
    }

    if (phase.value !== "active") {
      teacherLinkStatus.value = "waiting_for_admission";
      accountSignupError.value = "";
      return false;
    }

    if (!isIndividualGuestCall.value) {
      teacherLinkStatus.value = "not_applicable";
      accountSignupError.value = "";
      return false;
    }

    const generation = lifecycleGeneration;
    const requestId = joinRequest.value.id;
    const currentGuestSecret = guestSecret;
    accountSignupChecking.value = true;
    accountSignupError.value = "";

    try {
      const response = await api.videoCalls.getGuestStudentSignupEligibility(
        requestId,
        currentGuestSecret,
      );
      if (!isCurrentLifecycle(generation)) {
        return false;
      }

      teacherLinkStatus.value = response.teacher_link_status ?? null;
      return response.eligible === true;
    } catch (error) {
      if (!isCurrentLifecycle(generation)) {
        return false;
      }
      applyAccountSignupError(error);
      return false;
    } finally {
      if (isCurrentLifecycle(generation)) {
        accountSignupChecking.value = false;
      }
    }
  };

  const runGuestStudentSignup = async (
    request: (
      joinRequestId: string,
      currentGuestSecret: string,
    ) => Promise<VideoCallStudentSignupResponse>,
  ): Promise<VideoCallStudentSignupResponse | null> => {
    if (
      role.value !== "guest" ||
      phase.value !== "active" ||
      !joinRequest.value ||
      !guestSecret ||
      !isIndividualGuestCall.value
    ) {
      teacherLinkStatus.value =
        phase.value === "active" ? "not_applicable" : "waiting_for_admission";
      return null;
    }

    const generation = lifecycleGeneration;
    const requestId = joinRequest.value.id;
    const currentGuestSecret = guestSecret;
    accountSignupError.value = "";

    try {
      const response = await request(requestId, currentGuestSecret);
      if (!isCurrentLifecycle(generation)) {
        return null;
      }
      applyGuestAccountResponse(response);
      return response;
    } catch (error) {
      if (!isCurrentLifecycle(generation)) {
        return null;
      }
      applyAccountSignupError(error);
      return null;
    }
  };

  const signupGuestStudentWithPassword = async (
    account: VideoCallStudentPasswordSignupInput,
  ): Promise<VideoCallStudentSignupResponse | null> =>
    await runGuestStudentSignup(
      async (requestId, currentGuestSecret) =>
        await api.videoCalls.signupGuestStudentWithPassword(
          requestId,
          currentGuestSecret,
          account,
        ),
    );

  const signupGuestStudentWithGoogle = async (
    account: VideoCallStudentGoogleSignupInput,
  ): Promise<VideoCallStudentSignupResponse | null> =>
    await runGuestStudentSignup(
      async (requestId, currentGuestSecret) =>
        await api.videoCalls.signupGuestStudentWithGoogle(
          requestId,
          currentGuestSecret,
          account,
        ),
    );

  const prepareGuestMedia = async () => {
    setPrivacyActive(true);
    await requestMedia();
  };

  async function reconnect() {
    if ((!call.value && !joinRequest.value) || reconnectInFlight) return;

    const generation = lifecycleGeneration;
    reconnectInFlight = true;
    errorMessage.value = "";
    try {
      if (role.value === "host" && call.value) {
        await connectHost(generation);
        return;
      }

      if (role.value === "guest" && joinRequest.value && guestSecret) {
        const requestId = joinRequest.value.id;
        const currentGuestSecret = guestSecret;
        const response = await api.videoCalls.updateGuestConnection(
          requestId,
          currentGuestSecret,
          "connected",
        );
        if (!isCurrentLifecycle(generation)) return;
        const nextConnection = resolveConnection(response);
        if (nextConnection) await connectRuntime(nextConnection, generation);
      }
    } catch (error) {
      if (!isCurrentLifecycle(generation)) return;
      const status = (error as { response?: { status?: number } }).response
        ?.status;
      if ([401, 403, 404, 410].includes(status ?? 0)) {
        await finishLocally("ended");
        return;
      }
      connectionState.value = "reconnecting";
      reconnectRetryTimer = stopRetryTimer(reconnectRetryTimer);
      reconnectRetryTimer = setTimeout(() => {
        reconnectRetryTimer = null;
        if (isCurrentLifecycle(generation)) {
          reconnect();
        }
      }, 2_000);
    } finally {
      if (isCurrentLifecycle(generation)) {
        reconnectInFlight = false;
      }
    }
  }

  async function refreshRtcCredentials() {
    if (
      refreshingRtcCredentials ||
      phase.value !== "active" ||
      !connection.value
    ) {
      return;
    }

    const generation = lifecycleGeneration;
    const previousSignaling = connection.value.signaling;
    refreshingRtcCredentials = true;
    try {
      let response:
        | Awaited<ReturnType<typeof api.videoCalls.updateHostConnection>>
        | Awaited<ReturnType<typeof api.videoCalls.updateGuestConnection>>
        | null = null;

      if (role.value === "host" && call.value) {
        response = await api.videoCalls.updateHostConnection(
          call.value.id,
          "connected",
        );
      } else if (role.value === "guest" && joinRequest.value && guestSecret) {
        response = await api.videoCalls.updateGuestConnection(
          joinRequest.value.id,
          guestSecret,
          "connected",
        );
      }

      if (!isCurrentLifecycle(generation)) return;
      const nextConnection = resolveConnection(response);
      if (!nextConnection) {
        throw new Error("Configuração RTC ausente.");
      }

      connection.value = {
        ...nextConnection,
        signaling: previousSignaling,
        participants: participants.value,
      };
      runtime?.updateRtcConfiguration(nextConnection);
    } catch (error) {
      if (!isCurrentLifecycle(generation)) return;
      const status = (error as { response?: { status?: number } }).response
        ?.status;
      if ([401, 403, 404, 410].includes(status ?? 0)) {
        await finishLocally("ended");
        return;
      }
      rtcRefreshRetryTimer = stopRetryTimer(rtcRefreshRetryTimer);
      rtcRefreshRetryTimer = setTimeout(() => {
        rtcRefreshRetryTimer = null;
        if (isCurrentLifecycle(generation)) {
          refreshRtcCredentials();
        }
      }, 5_000);
    } finally {
      if (isCurrentLifecycle(generation)) {
        refreshingRtcCredentials = false;
      }
    }
  }

  const isLiveEnabledTrack = (track: MediaStreamTrack): boolean =>
    track.readyState !== "ended" && track.enabled;

  const publishEffectiveMediaState = () => {
    const localStream = runtime?.getLocalStream();
    runtime?.sendMediaUpdate(
      localStream?.getVideoTracks().some(isLiveEnabledTrack) ?? false,
      localStream?.getAudioTracks().some(isLiveEnabledTrack) ?? false,
    );
  };

  const clearCameraRecovery = () => {
    cameraRecoveryIntent = false;
    cameraRecoveryRetryPending = false;
  };

  const invalidateInputRecovery = (kind: VideoCallInputDeviceKind) => {
    inputRecoveryRevision[kind] += 1;
    if (kind === "audioinput") {
      microphoneRecoveryAttempt = null;
    } else {
      clearCameraRecovery();
      cameraRecoveryAttempt = null;
    }
  };

  const attemptCameraRecovery = (
    generation: number,
    options: { afterScreenShare: boolean },
  ): Promise<boolean> => {
    if (!isCurrentLifecycle(generation) || !cameraRecoveryIntent) {
      return Promise.resolve(false);
    }
    if (cameraRecoveryAttempt?.generation === generation) {
      return cameraRecoveryAttempt.promise;
    }

    const recoveryRevision = inputRecoveryRevision.videoinput;
    const recoveryIsCurrent = () =>
      isCurrentLifecycle(generation) &&
      cameraRecoveryIntent &&
      recoveryRevision === inputRecoveryRevision.videoinput;

    const shouldRetryAfterScreenShare =
      !options.afterScreenShare && runtime?.isScreenSharing() === true;
    const attempt: InputRecoveryAttempt = {
      generation,
      promise: Promise.resolve(false),
    };
    attempt.promise = (async () => {
      try {
        const activeRuntime = await ensureRuntime();
        if (!recoveryIsCurrent()) {
          return false;
        }

        let recoveredStream: MediaStream;
        if (selectedCameraDeviceId.value) {
          try {
            recoveredStream = await activeRuntime.switchInputDevice(
              "videoinput",
              selectedCameraDeviceId.value,
              true,
            );
          } catch (error) {
            if (!recoveryIsCurrent()) return false;
            if (
              !(error instanceof DOMException) ||
              !["NotFoundError", "OverconstrainedError"].includes(error.name)
            ) {
              throw error;
            }
            recoveredStream = await activeRuntime.ensureLocalMedia("video");
          }
        } else {
          recoveredStream = await activeRuntime.ensureLocalMedia("video");
        }
        if (!recoveryIsCurrent()) {
          return false;
        }
        if (
          !recoveredStream
            .getVideoTracks()
            .some((track) => track.readyState !== "ended")
        ) {
          throw new Error("A câmera recuperada não possui uma faixa ativa.");
        }

        cameraRecoveryIntent = false;
        cameraRecoveryRetryPending = false;
        cameraEnabled.value = true;
        activeRuntime.setTrackEnabled("video", true);
        mediaReady.value = true;
        publishEffectiveMediaState();
        refreshMediaDevices(generation, activeRuntime).catch(() => undefined);
        return true;
      } catch {
        if (!recoveryIsCurrent()) {
          return false;
        }

        cameraEnabled.value = false;
        runtime?.setTrackEnabled("video", false);
        publishEffectiveMediaState();
        if (shouldRetryAfterScreenShare) {
          cameraRecoveryRetryPending = true;
          return false;
        }

        clearCameraRecovery();
        screenShareError.value = CAMERA_RECOVERY_FAILED_MESSAGE;
        return false;
      } finally {
        if (cameraRecoveryAttempt === attempt) {
          cameraRecoveryAttempt = null;
        }
      }
    })();
    cameraRecoveryAttempt = attempt;
    return attempt.promise;
  };

  const retryCameraRecoveryAfterScreenShare = async (
    generation: number,
  ): Promise<void> => {
    const inFlightAttempt =
      cameraRecoveryAttempt?.generation === generation
        ? cameraRecoveryAttempt.promise
        : null;
    if (inFlightAttempt) await inFlightAttempt;
    if (
      !isCurrentLifecycle(generation) ||
      !cameraRecoveryIntent ||
      !cameraRecoveryRetryPending
    ) {
      return;
    }

    cameraRecoveryRetryPending = false;
    await attemptCameraRecovery(generation, { afterScreenShare: true });
  };

  const handleLocalCameraEnded = () => {
    if (!cameraEnabled.value || switchingDeviceKind.value === "videoinput") {
      publishEffectiveMediaState();
      return;
    }

    const generation = lifecycleGeneration;
    cameraRecoveryIntent = true;
    cameraRecoveryRetryPending = false;
    cameraEnabled.value = false;
    runtime?.setTrackEnabled("video", false);
    publishEffectiveMediaState();
    attemptCameraRecovery(generation, { afterScreenShare: false }).catch(
      () => undefined,
    );
  };

  const handleLocalMicrophoneEnded = () => {
    publishEffectiveMediaState();
    if (
      !microphoneEnabled.value ||
      microphoneRecoveryAttempt ||
      switchingDeviceKind.value === "audioinput"
    ) {
      return;
    }

    const generation = lifecycleGeneration;
    const recoveryRevision = inputRecoveryRevision.audioinput;
    const recoveryIsCurrent = () =>
      isCurrentLifecycle(generation) &&
      microphoneEnabled.value &&
      recoveryRevision === inputRecoveryRevision.audioinput;
    const attempt: InputRecoveryAttempt = {
      generation,
      promise: Promise.resolve(false),
    };
    attempt.promise = (async () => {
      try {
        const activeRuntime = await ensureRuntime();
        if (!recoveryIsCurrent()) {
          return false;
        }
        const stream = await activeRuntime.ensureLocalMedia("audio");
        if (!recoveryIsCurrent()) {
          return false;
        }
        if (!getAvailableMediaState(stream).microphoneAvailable) {
          throw new Error("O microfone recuperado não possui uma faixa ativa.");
        }
        mediaReady.value = true;
        activeRuntime.setTrackEnabled("audio", true);
        publishEffectiveMediaState();
        await refreshMediaDevices(generation, activeRuntime);
        return true;
      } catch {
        if (!recoveryIsCurrent()) {
          return false;
        }
        microphoneEnabled.value = false;
        runtime?.setTrackEnabled("audio", false);
        publishEffectiveMediaState();
        mediaError.value = MICROPHONE_RECOVERY_FAILED_MESSAGE;
        return false;
      } finally {
        if (microphoneRecoveryAttempt === attempt) {
          microphoneRecoveryAttempt = null;
        }
      }
    })();
    microphoneRecoveryAttempt = attempt;
    attempt.promise.catch(() => undefined);
  };

  const publishClaimedScreenShare = async (
    attempt: ScreenShareAttempt,
  ): Promise<void> => {
    try {
      const result = await runtime?.publishScreenShare();
      if (
        !result ||
        !isCurrentScreenShareAttempt(attempt) ||
        screenSharePhase.value !== "active" ||
        !isPresenting.value
      ) {
        return;
      }

      // A newer camera/media or peer topology revision already reconciles the
      // authorized screen. Its predecessor must not be mistaken for a total
      // publication failure.
      if (result.superseded) return;

      // A solo presenter is valid. Once remote peers exist, at least one must
      // have received the track; individual failures recover inside runtime.
      const screenPublishedPeerCount =
        result.screenPublishedPeerCount ?? result.publishedPeerCount;
      if (result.totalPeerCount > 0 && screenPublishedPeerCount === 0) {
        await finalizeScreenShare({
          notifyServer: true,
          clearLocalPresenter: true,
          errorMessage: SCREEN_SHARE_PUBLICATION_FAILED_MESSAGE,
        });
      }
    } catch {
      if (
        !isCurrentScreenShareAttempt(attempt) ||
        screenSharePhase.value !== "active" ||
        !isPresenting.value
      ) {
        return;
      }
      await finalizeScreenShare({
        notifyServer: true,
        clearLocalPresenter: true,
        errorMessage: SCREEN_SHARE_PUBLICATION_FAILED_MESSAGE,
      });
    }
  };

  const finalizeScreenShare = async (
    options: ScreenShareFinalizationOptions,
  ): Promise<void> => {
    const generation = lifecycleGeneration;
    const finalizationRevision = ++screenShareAttemptRevision;
    clearScreenShareAttempt();
    screenShareCaptureReady = false;
    screenShareReleasedInLifecycle = true;
    setScreenSharePhase("stopping");
    if (options.errorMessage !== undefined) {
      screenShareError.value = options.errorMessage;
    }
    if (
      options.clearLocalPresenter &&
      presenterParticipantId.value === connection.value?.participant.id
    ) {
      setPresenterState(null);
    }

    // Both capture release and the authoritative slot release start before the
    // first await, so the UI is usable even while peer unpublish is recovering.
    const stopPromise = runtime?.stopScreenShare().catch(() => undefined);
    if (options.notifyServer) {
      runtime?.sendScreenShareRelease();
    }
    setScreenSharePhase("idle");

    await stopPromise;
    if (
      !isCurrentLifecycle(generation) ||
      finalizationRevision !== screenShareAttemptRevision ||
      screenSharePhase.value !== "idle"
    ) {
      return;
    }
    await retryCameraRecoveryAfterScreenShare(generation);
  };

  const setInputDevice = async (
    kind: VideoCallInputDeviceKind,
    deviceId: string,
  ): Promise<void> => {
    if (!deviceId || switchingDeviceKind.value) return;

    invalidateInputRecovery(kind);

    const generation = lifecycleGeneration;
    const previousDeviceId =
      kind === "audioinput"
        ? selectedMicrophoneDeviceId.value
        : selectedCameraDeviceId.value;
    switchingDeviceKind.value = kind;
    mediaDevicesError.value = "";
    if (kind === "audioinput") {
      selectedMicrophoneDeviceId.value = deviceId;
    } else {
      selectedCameraDeviceId.value = deviceId;
    }

    try {
      const activeRuntime = await ensureRuntime();
      if (!isCurrentLifecycle(generation)) return;
      await activeRuntime.switchInputDevice(
        kind,
        deviceId,
        kind === "audioinput" ? microphoneEnabled.value : cameraEnabled.value,
      );
      if (!isCurrentLifecycle(generation)) return;

      if (kind === "audioinput") {
        selectedMicrophoneDeviceId.value =
          getActiveInputDeviceId(kind) || deviceId;
      } else {
        selectedCameraDeviceId.value = getActiveInputDeviceId(kind) || deviceId;
      }
      mediaReady.value = true;
      if (
        kind === "audioinput" &&
        mediaError.value === MICROPHONE_RECOVERY_FAILED_MESSAGE
      ) {
        mediaError.value = "";
      }
      publishEffectiveMediaState();
      await refreshMediaDevices(generation, activeRuntime);
    } catch (error) {
      if (!isCurrentLifecycle(generation)) return;
      if (kind === "audioinput") {
        selectedMicrophoneDeviceId.value = previousDeviceId;
      } else {
        selectedCameraDeviceId.value = previousDeviceId;
      }
      mediaDevicesStatus.value = "error";
      mediaDevicesError.value =
        error instanceof DOMException && error.name === "NotAllowedError"
          ? "Permissão negada para usar o dispositivo selecionado."
          : kind === "audioinput"
            ? "Não foi possível trocar o microfone. O anterior continua ativo."
            : "Não foi possível trocar a câmera. A anterior continua ativa.";
    } finally {
      if (isCurrentLifecycle(generation)) {
        switchingDeviceKind.value = null;
      }
    }
  };

  const toggleCamera = async () => {
    const generation = lifecycleGeneration;
    const nextEnabled = !cameraEnabled.value;
    const inFlightRecovery =
      cameraRecoveryAttempt?.generation === generation
        ? cameraRecoveryAttempt.promise
        : null;
    invalidateInputRecovery("videoinput");
    cameraEnabled.value = nextEnabled;
    if (nextEnabled && inFlightRecovery) {
      await inFlightRecovery;
      if (!isCurrentLifecycle(generation)) return;
    }
    const cameraAvailable = getAvailableMediaState(
      runtime?.getLocalStream() ?? null,
    ).cameraAvailable;
    if (nextEnabled && !cameraAvailable) {
      await requestMedia(generation, "video");
      if (!isCurrentLifecycle(generation)) return;
      if (
        !getAvailableMediaState(runtime?.getLocalStream() ?? null)
          .cameraAvailable
      ) {
        cameraEnabled.value = false;
      }
    }
    runtime?.setTrackEnabled("video", cameraEnabled.value);
    publishEffectiveMediaState();
    if (
      cameraEnabled.value &&
      screenShareError.value === CAMERA_RECOVERY_FAILED_MESSAGE
    ) {
      screenShareError.value = "";
    }
  };

  const toggleMicrophone = async () => {
    const generation = lifecycleGeneration;
    invalidateInputRecovery("audioinput");
    const nextEnabled = !microphoneEnabled.value;
    microphoneEnabled.value = nextEnabled;
    const microphoneAvailable = getAvailableMediaState(
      runtime?.getLocalStream() ?? null,
    ).microphoneAvailable;
    if (nextEnabled && !microphoneAvailable) {
      await requestMedia(generation, "audio");
      if (!isCurrentLifecycle(generation)) return;
      if (
        !getAvailableMediaState(runtime?.getLocalStream() ?? null)
          .microphoneAvailable
      ) {
        microphoneEnabled.value = false;
      }
    }
    runtime?.setTrackEnabled("audio", microphoneEnabled.value);
    publishEffectiveMediaState();
  };

  /** Captures first, then lets the backend authorize publication to peers. */
  const startScreenShare = async () => {
    if (screenSharePhase.value !== "idle" || isPresenting.value) return;

    screenShareError.value = "";
    if (!runtime?.isScreenShareSupported()) {
      screenShareError.value = getScreenShareDeniedMessage("unsupported");
      return;
    }

    const generation = lifecycleGeneration;
    const attempt: ScreenShareAttempt = {
      revision: ++screenShareAttemptRevision,
      lifecycle: generation,
      captureReady: false,
      claimSent: false,
    };
    screenShareAttempt = attempt;
    screenShareCaptureReady = false;
    screenShareReleasedInLifecycle = false;
    setScreenSharePhase("capturing");

    try {
      const captured = await runtime.captureScreenShare(
        screenShareAudioEnabled.value,
      );
      if (!isCurrentScreenShareAttempt(attempt)) return;
      if (!captured) {
        // The runtime normally reports the precise reason through its event.
        await finalizeScreenShare({
          notifyServer: false,
          clearLocalPresenter: true,
          ...(screenShareError.value
            ? {}
            : { errorMessage: getScreenShareDeniedMessage("failed") }),
        });
        return;
      }

      attempt.captureReady = true;
      screenShareCaptureReady = true;
      attempt.claimSent = true;
      setScreenSharePhase("awaiting-ack");
      screenShareAckTimer = setTimeout(() => {
        if (
          !isCurrentScreenShareAttempt(attempt) ||
          screenSharePhase.value !== "awaiting-ack"
        ) {
          return;
        }
        finalizeScreenShare({
          notifyServer: true,
          clearLocalPresenter: true,
          errorMessage: SCREEN_SHARE_ACK_TIMEOUT_MESSAGE,
        }).catch(() => undefined);
      }, SCREEN_SHARE_ACK_TIMEOUT_MS);

      if (!runtime.sendScreenShareClaim()) {
        attempt.claimSent = false;
        await finalizeScreenShare({
          notifyServer: false,
          clearLocalPresenter: true,
          errorMessage: getScreenShareDeniedMessage("failed"),
        });
      }
    } catch {
      if (!isCurrentScreenShareAttempt(attempt)) return;
      await finalizeScreenShare({
        notifyServer: attempt.claimSent,
        clearLocalPresenter: true,
        errorMessage: getScreenShareDeniedMessage("failed"),
      });
    }
  };

  const stopScreenShare = async () => {
    await finalizeScreenShare({
      notifyServer:
        screenShareAttempt?.claimSent === true || isPresenting.value,
      clearLocalPresenter: true,
    });
  };

  const toggleScreenShare = async () => {
    if (
      screenSharePhase.value !== "idle" ||
      isPresenting.value ||
      runtime?.isScreenSharing()
    ) {
      await stopScreenShare();
      return;
    }
    await startScreenShare();
  };

  const dismissScreenShareError = () => {
    screenShareError.value = "";
  };

  /** Large fills too much of the viewport for dragging to be worth offering. */
  const panelDraggable = computed(() => panelSize.value !== "large");
  const canGrowPanel = computed(() => panelSize.value !== "large");
  const canShrinkPanel = computed(() => panelSize.value !== "small");

  const setPanelSize = (size: VideoCallPanelSize) => {
    if (panelSize.value === size) return;
    panelSize.value = size;
    // Focus mode has no room for the chat, and its own footprint would be
    // overridden by the chat layout. Opening the chat grows the panel back out.
    if (size === "small") setChatOpen(false);
    // Smaller tiles are worth less remote video, so peers are told the size.
    runtime?.setVideoPresentationScale(size);
  };

  const stepPanelSize = (offset: 1 | -1) => {
    const nextSize = getAdjacentVideoCallPanelSize(panelSize.value, offset);
    if (nextSize) setPanelSize(nextSize);
  };

  const growPanel = () => stepPanelSize(1);
  const shrinkPanel = () => stepPanelSize(-1);

  const getLocalStream = (): MediaStream | null => {
    const version = runtimeVersion.value;
    if (version < 0) return null;
    return runtime?.getLocalStream() ?? null;
  };

  const getLocalScreenStream = (): MediaStream | null => {
    const version = runtimeVersion.value;
    if (version < 0) return null;
    return runtime?.getLocalScreenStream() ?? null;
  };

  /** Only meaningful while that participant holds the presenter slot. */
  const getRemoteScreenStream = (participantId: string): MediaStream | null => {
    const version = runtimeVersion.value;
    if (version < 0) return null;
    return runtime?.getRemoteScreenStream(participantId) ?? null;
  };

  const getRemoteStream = (participantId: string): MediaStream | null => {
    const version = runtimeVersion.value;
    if (version < 0) return null;
    return runtime?.getRemoteStream(participantId) ?? null;
  };

  const refreshPeerDiagnostics = async (): Promise<void> => {
    await runtime?.refreshPeerDiagnostics();
  };

  const setVideoPresentationActive = (active: boolean): void => {
    videoPresentationActive.value = active;
    runtime?.setVideoPresentationActive(active);
  };

  /** Lets the embedded page declare a rendered size without owning panelSize. */
  const setVideoPresentationScale = (scale: VideoCallPanelSize): void => {
    runtime?.setVideoPresentationScale(scale);
  };

  /** Null means every participant has a tile; focus mode narrows the list. */
  const setVisibleParticipants = (participantIds: string[] | null): void => {
    runtime?.setVisibleParticipants(participantIds);
  };

  const getParticipantVolume = (participantId: string): number =>
    participantVolumePercent.value[participantId] ??
    DEFAULT_PARTICIPANT_VOLUME_PERCENT;

  const setParticipantVolume = (
    participantId: string,
    volumePercent: number,
  ): void => {
    const normalizedVolumePercent = Number.isNaN(volumePercent)
      ? DEFAULT_PARTICIPANT_VOLUME_PERCENT
      : Math.round(Math.min(100, Math.max(0, volumePercent)));
    const nextParticipantVolumePercent = {
      ...participantVolumePercent.value,
    };

    if (normalizedVolumePercent === DEFAULT_PARTICIPANT_VOLUME_PERCENT) {
      delete nextParticipantVolumePercent[participantId];
    } else {
      nextParticipantVolumePercent[participantId] = normalizedVolumePercent;
    }

    participantVolumePercent.value = nextParticipantVolumePercent;
  };

  async function finishLocally(
    nextPhase: "idle" | "denied" | "ended" | "error" = "idle",
    options: { preserveError?: boolean } = {},
  ) {
    beginLifecycle();
    await runtime?.disconnect({ stopMedia: true });
    setHostInvitation(null);
    clearStoredVideoCallGuestSession();
    guestSecret = "";
    guestToken = "";
    connection.value = null;
    connectionState.value = "disconnected";
    participants.value = [];
    peerStates.value = {};
    peerDiagnostics.value = {};
    remoteVideoQuality.value = {};
    joinRequests.value = [];
    joinRequest.value = null;
    call.value = null;
    preview.value = null;
    role.value = null;
    mediaReady.value = false;
    participantVolumePercent.value = {};
    cameraDevices.value = [];
    microphoneDevices.value = [];
    selectedCameraDeviceId.value = "";
    selectedMicrophoneDeviceId.value = "";
    mediaDevicesStatus.value = "idle";
    mediaDevicesError.value = "";
    switchingDeviceKind.value = null;
    loading.value = false;
    removingParticipantId.value = null;
    participantRemovalError.value = "";
    accountLinking.value = false;
    accountLinkError.value = "";
    guestMediaRestoration = null;
    guestBackgroundNavigationEnabled.value = false;
    restoredIndividualGuestCall.value = null;
    panelSize.value = DEFAULT_VIDEO_CALL_PANEL_SIZE;
    clearChat();
    if (!options.preserveError) {
      errorMessage.value = "";
    }
    phase.value = nextPhase;
    setPrivacyActive(false);
  }

  const endHostCall = async () => {
    if (!call.value || role.value !== "host") return;
    const generation = lifecycleGeneration;
    const callId = call.value.id;
    const previousChatState = {
      capability: chatCapability.value,
      messages: chatMessages.value,
      open: chatOpen.value,
      visible: chatVisible.value,
      unreadCount: chatUnreadCount.value,
    };
    clearChat();
    loading.value = true;
    errorMessage.value = "";
    try {
      await api.videoCalls.endCall(callId);
      if (!isCurrentLifecycle(generation)) return;
      await finishLocally("ended");
    } catch (error) {
      if (!isCurrentLifecycle(generation)) return;
      chatCapability.value = previousChatState.capability;
      chatMessages.value = previousChatState.messages.map((message) =>
        message.status === "pending"
          ? {
              ...message,
              status: "failed",
              error:
                "A confirmação não chegou. Verifique a conexão e tente novamente.",
            }
          : message,
      );
      chatOpen.value = previousChatState.open;
      chatVisible.value = previousChatState.visible;
      chatUnreadCount.value = previousChatState.unreadCount;
      errorMessage.value = getApiErrorMessage(
        error,
        "Não foi possível encerrar a chamada.",
      );
    } finally {
      if (isCurrentLifecycle(generation)) {
        loading.value = false;
      }
    }
  };

  const leaveGuestCall = async () => {
    const generation = lifecycleGeneration;
    clearChat();
    try {
      if (joinRequest.value && guestSecret) {
        await api.videoCalls.updateGuestConnection(
          joinRequest.value.id,
          guestSecret,
          "left",
        );
      }
    } catch {
      // Local teardown must not be held hostage by a failed best-effort leave.
    } finally {
      if (isCurrentLifecycle(generation)) {
        await finishLocally("ended");
      }
    }
  };

  const handleIdentityWillChange = async () => {
    const activeCallId = role.value === "host" ? call.value?.id : undefined;
    const activeJoinRequestId =
      role.value === "guest" ? joinRequest.value?.id : undefined;
    const activeGuestSecret = role.value === "guest" ? guestSecret : "";
    const serverTeardown = activeCallId
      ? api.videoCalls
          .endCall(activeCallId, {
            skipRefreshWait: true,
            skipRefreshRetry: true,
            suppressAuthFailureRedirect: true,
          })
          .catch(() => undefined)
      : activeJoinRequestId && activeGuestSecret
        ? api.videoCalls
            .updateGuestConnection(
              activeJoinRequestId,
              activeGuestSecret,
              "left",
            )
            .catch(() => undefined)
        : Promise.resolve();

    await finishLocally("idle");
    initialized.value = false;
    await serverTeardown;
  };

  const dismissEndedState = () => {
    phase.value = "idle";
    errorMessage.value = "";
  };

  const exitGuestPage = async () => {
    if (role.value !== "guest") return;

    if (phase.value === "active" || phase.value === "waiting") {
      await leaveGuestCall();
      return;
    }

    await finishLocally("idle");
  };

  return {
    roomsSnapshot: roomsController.roomsSnapshot,
    roomsLoading: roomsController.roomsLoading,
    roomsError: roomsController.roomsError,
    roomsMovingParticipantId: roomsController.roomsMovingParticipantId,
    roomsOpen: roomsController.roomsOpen,
    roomsAnnouncementDismissedId: roomsController.roomsAnnouncementDismissedId,
    setRoomsOpen: roomsController.setRoomsOpen,
    refreshRooms: roomsController.refreshRooms,
    saveRooms: roomsController.saveRooms,
    openRooms: roomsController.openRooms,
    closeRooms: roomsController.closeRooms,
    moveToRoom: roomsController.moveToRoom,
    returnToMainRoom: roomsController.returnToMainRoom,
    requestRoomHelp: roomsController.requestRoomHelp,
    cancelRoomHelp: roomsController.cancelRoomHelp,
    sendRoomAnnouncement: roomsController.sendRoomAnnouncement,
    dismissRoomAnnouncement: roomsController.dismissRoomAnnouncement,
    phase,
    role,
    call,
    preview,
    joinRequest,
    joinRequests,
    participants,
    connection,
    connectionState,
    peerStates,
    peerDiagnostics,
    remoteVideoQuality,
    chatCapability,
    chatMessages,
    chatOpen,
    chatVisible,
    chatUnreadCount,
    panelSize,
    panelDraggable,
    canGrowPanel,
    canShrinkPanel,
    setPanelSize,
    growPanel,
    shrinkPanel,
    guestBackgroundNavigationEnabled,
    loading,
    initialized,
    cameraEnabled,
    microphoneEnabled,
    screenShareCapability,
    canShareScreen,
    isPresenting,
    presenter,
    presenterParticipantId,
    activeSpeakerId,
    screenShareBusy,
    screenSharePhase,
    screenSharePending,
    screenShareError,
    participantVolumePercent,
    cameraDevices,
    microphoneDevices,
    selectedCameraDeviceId,
    selectedMicrophoneDeviceId,
    mediaDevicesStatus,
    mediaDevicesError,
    switchingDeviceKind,
    mediaReady,
    mediaError,
    errorMessage,
    removingParticipantId,
    participantRemovalError,
    accountLinking,
    accountLinkError,
    teacherLinkStatus,
    accountSignupChecking,
    accountSignupError,
    runtimeVersion,
    limits,
    connectedParticipants,
    participantCount,
    pendingJoinRequests,
    atCapacity,
    hasActiveSession,
    canContinueGuestInBackground,
    isIndividualGuestCall,
    chatEnabled,
    chatMaxBodyLength,
    shareUrl,
    initializeHost,
    prepareHostCall,
    cancelHostPreparation,
    startHostCall,
    rotateInvitation,
    approveJoinRequest,
    denyJoinRequest,
    clearParticipantRemovalError,
    removeParticipant,
    initializeGuest,
    enableGuestBackgroundNavigation,
    restoreGuestBackgroundSession,
    prepareGuestMedia,
    joinAsGuest,
    linkGuestAccount,
    clearGuestAccountErrors,
    checkGuestStudentSignupEligibility,
    signupGuestStudentWithPassword,
    signupGuestStudentWithGoogle,
    toggleCamera,
    toggleMicrophone,
    toggleScreenShare,
    startScreenShare,
    stopScreenShare,
    dismissScreenShareError,
    refreshMediaDevices,
    setInputDevice,
    getLocalStream,
    getLocalScreenStream,
    getRemoteStream,
    getRemoteScreenStream,
    refreshPeerDiagnostics,
    setVideoPresentationActive,
    setVideoPresentationScale,
    setVisibleParticipants,
    setChatOpen,
    setChatVisible,
    sendChatMessage,
    retryChatMessage,
    getParticipantVolume,
    setParticipantVolume,
    endHostCall,
    leaveGuestCall,
    handleIdentityWillChange,
    exitGuestPage,
    dismissEndedState,
  };
});
